import React, { useEffect, useState, useRef } from 'react';
import { dispatchAPI, contractsAPI, inventoryAPI, subcontractAPI, suppliersAPI, programsAPI } from '@/lib/api';
import { EntityLink } from '@/components/EntityLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/SearchableSelect';
import DispatchDashboard from './DispatchDashboard';
import QuickDispatch from './QuickDispatch';
import DispatchPlanner from './DispatchPlanner';
import { 
  Plus, Download, FileCheck, Trash2, Truck, Package, Receipt, 
  ChevronLeft, ChevronRight, Link2, Mail, FileText, Upload, Eye, ClipboardList, Recycle, Scale, AlertTriangle, ShieldCheck, RotateCcw, Wrench, Loader2, Ban, Pencil, Check
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';

const CURRENCY_SYMBOLS = { INR: '₹', USD: '$', EUR: '€' };
// Display a manifest/DC line rate in its Customer-PO currency (foreign). Falls
// back to the INR rate when no foreign value is present (INR POs / legacy rows).
const fmtLineRate = (item) => {
  const sym = CURRENCY_SYMBOLS[item?.currency] || '₹';
  const val = item?.currency && item.currency !== 'INR'
    ? Number(item.rate_foreign || 0)
    : Number(item.rate || 0);
  return `${sym}${val.toLocaleString('en-IN')}`;
};

export default function DispatchPage() {
  const { user } = useAuth();
  const canIssueCoc = user && (user.role === 'admin' || user.role === 'quality_manager');
  const isAdmin = user && user.role === 'admin';
  const [activeTab, setActiveTab] = useState('plan');
  const [challans, setChallans] = useState([]);
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [manifestTotal, setManifestTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [manifestPage, setManifestPage] = useState(1);
  const limit = 20;

  // Dashboard stats
  const [stats, setStats] = useState({});

  // Customers
  const [customers, setCustomers] = useState([]);

  // Create Manifest dialog
  const [createManifestOpen, setCreateManifestOpen] = useState(false);
  const [manifestCustomerId, setManifestCustomerId] = useState('');
  const [manifestRemarks, setManifestRemarks] = useState('');
  const [creatingManifest, setCreatingManifest] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [driftWarnings, setDriftWarnings] = useState([]);
  const [previewStep, setPreviewStep] = useState(false); // false=select customer, true=preview items
  // Item-type filter applied when fetching the preview. Default = Finished Good only.
  const SELECTABLE_ITEM_TYPES = ['Finished Good', 'Consumable', 'Fixed Asset', 'Raw Material', 'Semi-Finished Part', 'Job Work Item'];
  const [includeItemTypes, setIncludeItemTypes] = useState(['Finished Good']);
  const [extraGodownIds, setExtraGodownIds] = useState([]);
  const [cancelChallanTarget, setCancelChallanTarget] = useState(null);
  // Fixed assets are excluded from dispatch item pickers unless explicitly included.
  const [includeFixedAssets, setIncludeFixedAssets] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingChallan, setCancellingChallan] = useState(false);

  // View Manifest dialog
  const [viewManifestOpen, setViewManifestOpen] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [loadingManifest, setLoadingManifest] = useState(false);

  // Email Manifest dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to_email: 'accounts3@bhpl.co.in',
    cc_email: '',
    subject: '',
    message: '',
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Create Challans confirmation
  const [creatingChallans, setCreatingChallans] = useState(false);

  // Create DC dialog (manual)
  const [createOpen, setCreateOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [stock, setStock] = useState([]);
  const [creating, setCreating] = useState(false);
  const [dcForm, setDcForm] = useState({
    customer_id: '', dispatch_type: 'delivery_challan', invoice_number: '', remarks: '', inbound_challan_id: '',
  });
  const [dcItems, setDcItems] = useState([{ item_id: '', quantity: '', godown_id: '', remarks: '' }]);
  const [openInboundChallans, setOpenInboundChallans] = useState([]);

  // Reconcile dialog
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileChallan, setReconcileChallan] = useState(null);
  const [reconcileForm, setReconcileForm] = useState({ invoice_number: '', reconciliation_type: 'invoice', remarks: '' });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');
  const [manifestStatusFilter, setManifestStatusFilter] = useState('all');
  const [emailFilter, setEmailFilter] = useState('all');

  // ================= CUSTOMER EMAIL STATE =================
  const [customerEmailOpen, setCustomerEmailOpen] = useState(false);
  const [emailChallans, setEmailChallans] = useState([]);
  const [selectedEmailChallans, setSelectedEmailChallans] = useState([]);
  const [customerEmailForm, setCustomerEmailForm] = useState({
    to_email: '',
    cc_email: '',
    subject: '',
    message: '',
    courier_name: '',
    tracking_number: '',
    include_challan_pdf: true,
    include_coc_bundle: false,
  });
  const [sendingCustomerEmail, setSendingCustomerEmail] = useState(false);
  const [emailCustomerId, setEmailCustomerId] = useState('');
  const [showAlreadyEmailed, setShowAlreadyEmailed] = useState(false);
  const [showOldUnsent, setShowOldUnsent] = useState(false);
  const [includeNonInvoiced, setIncludeNonInvoiced] = useState(false);
  const [includeNonReconciled, setIncludeNonReconciled] = useState(false);

  // ================= ONE-CLICK DISPATCH STATE =================
  const [oneClickOpen, setOneClickOpen] = useState(false);
  const [oneClickForm, setOneClickForm] = useState({
    to_email: '', cc_email: '', subject: '', message: '', courier_name: '', tracking_number: '',
  });
  const [oneClickSending, setOneClickSending] = useState(false);

  // ================= SCRAP STATE =================
  const [scrapStats, setScrapStats] = useState({});
  const [pendingScrap, setPendingScrap] = useState([]);
  const [scrapReturns, setScrapReturns] = useState([]);
  const [scrapPage, setScrapPage] = useState(1);
  const [scrapTotal, setScrapTotal] = useState(0);

  // Create Scrap Return dialog
  const [createScrapReturnOpen, setCreateScrapReturnOpen] = useState(false);
  const [scrapReturnCustomerId, setScrapReturnCustomerId] = useState('');
  const [scrapReturnRemarks, setScrapReturnRemarks] = useState('');
  const [selectedScrapEntries, setSelectedScrapEntries] = useState([]);
  const [creatingScrapReturn, setCreatingScrapReturn] = useState(false);
  const [customerPendingScrap, setCustomerPendingScrap] = useState([]);
  const [selectedCustomerDc, setSelectedCustomerDc] = useState(''); // Track selected Customer DC for single-DC constraint
  const [scrapFilterDc, setScrapFilterDc] = useState('');
  const [scrapFilterPo, setScrapFilterPo] = useState('');
  const [scrapShowAll, setScrapShowAll] = useState(false);
  const [loadingCustomerScrap, setLoadingCustomerScrap] = useState(false);

  // ================= SUPPLIER RETURN STATE =================
  const [supplierReturnOpen, setSupplierReturnOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierReturnId, setSupplierReturnId] = useState('');
  const [supplierReturnItems, setSupplierReturnItems] = useState([]);
  const [supplierReturnRemarks, setSupplierReturnRemarks] = useState('');
  const [supplierReturnPreviewStep, setSupplierReturnPreviewStep] = useState(false);
  const [supplierReturnPreviewLoading, setSupplierReturnPreviewLoading] = useState(false);
  const [creatingSupplierReturn, setCreatingSupplierReturn] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadCustomers();
    loadSuppliers();
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard();
    } else if (activeTab === 'manifests') {
      loadManifests();
    } else if (activeTab === 'challans') {
      loadChallans();
    } else if (activeTab === 'scrap') {
      loadScrapData();
    } else if (activeTab === 'supplier_returns') {
      loadSupplierReturnChallans();
    }
  }, [activeTab, page, manifestPage, scrapPage, statusFilter, manifestStatusFilter, emailFilter]);

  const loadDashboard = async () => {
    try {
      const res = await dispatchAPI.dashboard();
      setStats(res.data);
    } catch { /* ignore */ }
  };

  const loadCustomers = async () => {
    try {
      const res = await contractsAPI.activeCustomers();
      setCustomers(res.data || []);
    } catch { /* ignore */ }
  };

  const loadSuppliers = async () => {
    try {
      const res = await suppliersAPI.approvedList({ include_non_as: true });
      setSuppliers(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  };

  const loadChallans = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter && statusFilter !== 'all') {
        params.reconciliation_status = statusFilter;
      }
      if (emailFilter && emailFilter !== 'all') {
        params.email_status = emailFilter;
      }
      const res = await dispatchAPI.listChallans(params);
      setChallans(res.data.challans || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load dispatches'); }
    finally { setLoading(false); }
  };

  const loadManifests = async () => {
    setLoading(true);
    try {
      const params = { page: manifestPage, limit };
      if (manifestStatusFilter && manifestStatusFilter !== 'all') {
        params.status = manifestStatusFilter;
      }
      const res = await dispatchAPI.listManifests(params);
      setManifests(res.data.manifests || []);
      setManifestTotal(res.data.total || 0);
    } catch { toast.error('Failed to load manifests'); }
    finally { setLoading(false); }
  };

  // ================= SCRAP FUNCTIONS =================
  const loadScrapData = async () => {
    setLoading(true);
    try {
      const [dashRes, pendingRes, returnsRes] = await Promise.all([
        dispatchAPI.scrapDashboard().catch(() => ({ data: {} })),
        dispatchAPI.listPendingScrap().catch(() => ({ data: [] })),
        dispatchAPI.listScrapReturns({ page: scrapPage, limit }).catch(() => ({ data: { returns: [], total: 0 } })),
      ]);
      setScrapStats(dashRes.data || {});
      setPendingScrap(pendingRes.data || []);
      setScrapReturns(returnsRes.data.returns || []);
      setScrapTotal(returnsRes.data.total || 0);
    } catch { toast.error('Failed to load scrap data'); }
    finally { setLoading(false); }
  };

  const openCreateScrapReturn = () => {
    setScrapReturnCustomerId('');
    setScrapReturnRemarks('');
    setSelectedScrapEntries([]);
    setCustomerPendingScrap([]);
    setSelectedCustomerDc('');
    setScrapFilterDc('');
    setScrapFilterPo('');
    setScrapShowAll(false);
    setCreateScrapReturnOpen(true);
  };

  const loadCustomerScrap = async (customerId, opts = {}) => {
    const cid = customerId !== undefined ? customerId : scrapReturnCustomerId;
    if (!cid) { setCustomerPendingScrap([]); return; }
    const showAll = opts.showAll !== undefined ? opts.showAll : scrapShowAll;
    const dcFilter = opts.dc !== undefined ? opts.dc : scrapFilterDc;
    const poFilter = opts.po !== undefined ? opts.po : scrapFilterPo;
    setLoadingCustomerScrap(true);
    try {
      const params = { customer_id: cid };
      if (dcFilter?.trim()) params.customer_challan_number = dcFilter.trim();
      if (poFilter?.trim()) params.customer_po_number = poFilter.trim();
      if (showAll) params.include_all = true;
      const res = await dispatchAPI.listPendingScrap(params);
      const entries = [];
      (res.data || []).forEach(g => {
        (g.entries || []).forEach(e => {
          entries.push({ ...e, customer_id: g.customer_id, customer_name: g.customer_name, selected: false });
        });
      });
      setCustomerPendingScrap(entries);
    } catch { /* ignore */ }
    finally { setLoadingCustomerScrap(false); }
  };

  const onScrapCustomerSelect = async (customerId) => {
    setScrapReturnCustomerId(customerId);
    setSelectedScrapEntries([]);
    setCustomerPendingScrap([]);
    setSelectedCustomerDc('');
    setScrapFilterDc('');
    setScrapFilterPo('');
    setScrapShowAll(false);
    await loadCustomerScrap(customerId, { showAll: false, dc: '', po: '' });
  };

  const toggleScrapEntry = (entryId) => {
    const entry = customerPendingScrap.find(e => e.id === entryId);
    const entryDc = entry?.customer_challan_number || '';
    
    setSelectedScrapEntries(prev => {
      if (prev.includes(entryId)) {
        // Deselecting: remove entry and recalculate selectedCustomerDc
        const newSelected = prev.filter(id => id !== entryId);
        if (newSelected.length === 0) {
          setSelectedCustomerDc('');
        } else {
          // Keep the DC of remaining entries (should all be same)
          const remainingEntry = customerPendingScrap.find(e => e.id === newSelected[0]);
          setSelectedCustomerDc(remainingEntry?.customer_challan_number || '');
        }
        return newSelected;
      } else {
        // Selecting: check DC constraint
        if (selectedCustomerDc && entryDc && selectedCustomerDc !== entryDc) {
          toast.error(`All entries must belong to the same Customer DC (${selectedCustomerDc}). Cannot add entry from DC: ${entryDc}`);
          return prev;
        }
        // Set the DC if this is the first selection with a DC
        if (!selectedCustomerDc && entryDc) {
          setSelectedCustomerDc(entryDc);
        }
        return [...prev, entryId];
      }
    });
  };

  const handleCreateScrapReturn = async () => {
    if (!scrapReturnCustomerId) {
      toast.error('Select a customer');
      return;
    }
    if (selectedScrapEntries.length === 0) {
      toast.error('Select at least one scrap entry');
      return;
    }

    setCreatingScrapReturn(true);
    try {
      const itemsToReturn = selectedScrapEntries.map(entryId => {
        const entry = customerPendingScrap.find(e => e.id === entryId);
        return { entry_id: entryId, weight: entry?.actual_weight || 0 };
      });

      const res = await dispatchAPI.createScrapReturn({
        customer_id: scrapReturnCustomerId,
        items: itemsToReturn,
        remarks: scrapReturnRemarks,
      });
      toast.success(`Scrap return ${res.data.return_number} created with ${res.data.total_weight} kg`);
      setCreateScrapReturnOpen(false);
      loadScrapData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create scrap return');
    } finally {
      setCreatingScrapReturn(false);
    }
  };

  const downloadScrapReturnPDF = async (returnId, returnNumber) => {
    try {
      toast.info('Generating PDF...');
      const res = await dispatchAPI.downloadScrapReturnPDF(returnId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Scrap_Return_${returnNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
  };

  // ================= SUPPLIER RETURN FUNCTIONS =================
  const [supplierReturnChallans, setSupplierReturnChallans] = useState([]);
  const [supplierReturnTotal, setSupplierReturnTotal] = useState(0);

  const loadSupplierReturnChallans = async () => {
    setLoading(true);
    try {
      const params = { challan_category: 'supplier_return', page, limit };
      if (statusFilter && statusFilter !== 'all') params.reconciliation_status = statusFilter;
      const res = await dispatchAPI.listChallans(params);
      setSupplierReturnChallans(res.data.challans || []);
      setSupplierReturnTotal(res.data.total || 0);
    } catch { toast.error('Failed to load supplier return challans'); }
    finally { setLoading(false); }
  };

  const openSupplierReturnDialog = () => {
    setSupplierReturnId('');
    setSupplierReturnItems([]);
    setSupplierReturnRemarks('');
    setSupplierReturnPreviewStep(false);
    setSupplierReturnOpen(true);
  };

  const handleSupplierReturnPreview = async () => {
    if (!supplierReturnId) { toast.error('Select a supplier'); return; }
    setSupplierReturnPreviewLoading(true);
    try {
      const res = await dispatchAPI.supplierReturnPreview({ supplier_id: supplierReturnId });
      const items = (res.data.items || []).map(item => ({ ...item, selected: true }));
      if (items.length === 0) {
        toast.error('No quarantined or rejected items found for this supplier');
        return;
      }
      setSupplierReturnItems(items);
      setSupplierReturnPreviewStep(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview supplier return items');
    } finally { setSupplierReturnPreviewLoading(false); }
  };

  const toggleSupplierReturnItem = (idx) => {
    setSupplierReturnItems(prev => prev.map((item, i) => i === idx ? { ...item, selected: !item.selected } : item));
  };

  const updateSupplierReturnItemQty = (idx, qty) => {
    setSupplierReturnItems(prev => prev.map((item, i) => i === idx ? { ...item, quantity: parseFloat(qty) || 0 } : item));
  };

  const handleCreateSupplierReturn = async () => {
    const selectedItems = supplierReturnItems.filter(i => i.selected && i.quantity > 0);
    if (selectedItems.length === 0) { toast.error('Select at least one item'); return; }
    setCreatingSupplierReturn(true);
    try {
      const res = await dispatchAPI.supplierReturnCreateManifest({
        supplier_id: supplierReturnId,
        remarks: supplierReturnRemarks,
        selected_items: selectedItems.map(i => ({
          item_id: i.item_id,
          quantity: i.quantity,
          godown_id: i.godown_id,
          inbound_challan_id: '',
          inbound_challan_number: '',
        })),
      });
      toast.success(`Supplier return manifest ${res.data.manifest_number} created`);
      setSupplierReturnOpen(false);
      loadDashboard();
      // Auto-create challans from the manifest
      try {
        const challanRes = await dispatchAPI.supplierReturnCreateChallans(res.data.id);
        toast.success(challanRes.data.message);
        if (activeTab === 'supplier_returns') loadSupplierReturnChallans();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Manifest created but challan creation failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create supplier return');
    } finally { setCreatingSupplierReturn(false); }
  };

  // ================= MANIFEST FUNCTIONS =================

  const submitCancelChallan = async () => {
    if (!cancelChallanTarget) return;
    if (!cancelReason.trim()) { toast.error('Please enter a cancellation reason'); return; }
    setCancellingChallan(true);
    try {
      const res = await dispatchAPI.cancelChallan(cancelChallanTarget.id, { reason: cancelReason.trim() });
      toast.success(res.data?.message || 'Challan cancelled and stock restored');
      setCancelChallanTarget(null);
      setCancelReason('');
      loadChallans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel challan');
    } finally {
      setCancellingChallan(false);
    }
  };

  const openCreateManifest = () => {
    setManifestCustomerId('');
    setManifestRemarks('');
    setPreviewItems([]);
    setPreviewStep(false);
    setIncludeItemTypes(['Finished Good']);
    setExtraGodownIds([]);
    if (!godowns || godowns.length === 0) {
      inventoryAPI.listGodowns().then(r => setGodowns(r.data || [])).catch(() => {});
    }
    setCreateManifestOpen(true);
  };

  const handlePreviewManifest = async () => {
    if (!manifestCustomerId) { toast.error('Select a customer'); return; }
    setPreviewLoading(true);
    setDriftWarnings([]);
    try {
      const res = await dispatchAPI.previewManifest({
        customer_id: manifestCustomerId,
        include_item_types: includeItemTypes,
        extra_godown_ids: extraGodownIds,
      });
      const items = (res.data.items || []).map(item => ({ ...item, selected: true }));
      setDriftWarnings(res.data.drift_warnings || []);
      if (items.length === 0) {
        toast.error('No items ready for dispatch found for the selected types');
        return;
      }
      setPreviewItems(items);
      setPreviewStep(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch preview items');
    } finally { setPreviewLoading(false); }
  };

  const togglePreviewItem = (idx) => {
    setPreviewItems(prev => prev.map((item, i) => i === idx ? { ...item, selected: !item.selected } : item));
  };

  const updatePreviewItemQty = (idx, qty) => {
    setPreviewItems(prev => prev.map((item, i) => i === idx ? { ...item, quantity: parseFloat(qty) || 0 } : item));
  };

  // Admin-only manual correction of trace fields on the manifest preview.
  // `editingPreviewIdx` holds the row index currently in edit mode (or null).
  const [editingPreviewIdx, setEditingPreviewIdx] = useState(null);
  const updatePreviewItemField = (idx, field, value) => {
    setPreviewItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: value };
      // Keep the displayed trace id and the customer WO# in lock-step — they
      // represent the same value (customer work-order number) for FOC returns.
      if (field === 'traceability_id') next.customer_wo_number = value;
      if (field === 'customer_wo_number') next.traceability_id = value;
      return next;
    }));
  };

  const handleCreateManifest = async () => {
    const selectedItems = previewItems.filter(i => i.selected && i.quantity > 0);
    if (selectedItems.length === 0) { toast.error('Select at least one item'); return; }
    setCreatingManifest(true);
    try {
      const res = await dispatchAPI.createManifest({
        customer_id: manifestCustomerId,
        remarks: manifestRemarks,
        include_item_types: includeItemTypes,
        extra_godown_ids: extraGodownIds,
        selected_items: selectedItems.map(i => ({
          item_id: i.item_id,
          quantity: i.quantity,
          godown_id: i.godown_id,
          inbound_challan_id: i.inbound_challan_id || '',
          inbound_challan_number: i.inbound_challan_number || '',
          quality_status: i.quality_status || '',
          traceability_id: i.traceability_id || '',
          traceability_id_description: i.traceability_id_description || '',
          customer_wo_number: i.customer_wo_number || '',
          trace_lot_id: i.trace_lot_id || '',
          trace_type: i.trace_type || '',
          wo_number: i.wo_number || '',
          po_numbers: i.po_numbers || '',
          customer_challan_number: i.customer_challan_number || '',
          customer_po_number: i.customer_po_number || '',
          is_foc: !!i.is_foc,
          rate: parseFloat(i.rate) || 0,
          currency: i.currency || 'INR',
          conversion_factor: parseFloat(i.conversion_factor) || 1,
          rate_foreign: parseFloat(i.rate_foreign) || 0,
        })),
      });
      toast.success(`Manifest ${res.data.manifest_number} created with ${res.data.total_items} items`);
      setCreateManifestOpen(false);
      loadManifests();
      loadDashboard();
      viewManifest(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create manifest');
    } finally { setCreatingManifest(false); }
  };

  const viewManifest = async (manifestId) => {
    setLoadingManifest(true);
    setViewManifestOpen(true);
    try {
      const res = await dispatchAPI.getManifest(manifestId);
      setSelectedManifest(res.data);
    } catch (err) {
      toast.error('Failed to load manifest details');
    } finally {
      setLoadingManifest(false);
    }
  };

  const downloadManifestPDF = async (manifestId, manifestNumber) => {
    try {
      toast.info('Generating PDF...');
      const res = await dispatchAPI.downloadManifestPDF(manifestId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Manifest_${manifestNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
  };

  const downloadChallanPDF = async (challanId, dcNumber) => {
    try {
      toast.info('Generating challan PDF...');
      const res = await dispatchAPI.downloadPDF(challanId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Challan_${dcNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Challan PDF downloaded');
    } catch { toast.error('Failed to download challan PDF'); }
  };

  const openEmailDialog = () => {
    if (!selectedManifest) return;
    // Pre-populate with prior recipient if this is a resend, else default accounts mailbox
    const prevTo = selectedManifest.emailed_to;
    const prevCc = Array.isArray(selectedManifest.emailed_cc)
      ? selectedManifest.emailed_cc.filter(e => !['toolroom@bhpl.co.in', 'ae@bhpl.co.in'].includes((e || '').toLowerCase())).join(', ')
      : '';
    setEmailForm({
      to_email: prevTo || 'accounts3@bhpl.co.in',
      cc_email: prevCc || '',
      subject: `Dispatch Manifest ${selectedManifest.manifest_number} - ${selectedManifest.customer_name}`,
      message: `Dear Team,\n\nPlease find attached the Dispatch Manifest for ${selectedManifest.customer_name}.\n\nManifest Number: ${selectedManifest.manifest_number}\nTotal Items: ${selectedManifest.total_items}\nTotal Quantity: ${selectedManifest.total_quantity}\n\nRegards`,
    });
    setEmailDialogOpen(true);
  };

  const handleEmailManifest = async () => {
    if (!selectedManifest) return;
    if (!emailForm.to_email) {
      toast.error('Enter recipient email');
      return;
    }
    setSendingEmail(true);
    try {
      await dispatchAPI.emailManifest(selectedManifest.id, emailForm);
      toast.success('Manifest emailed successfully');
      setEmailDialogOpen(false);
      const res = await dispatchAPI.getManifest(selectedManifest.id);
      setSelectedManifest(res.data);
      loadManifests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCreateChallansFromManifest = async () => {
    if (!selectedManifest) return;
    if (selectedManifest.status === 'challans_created') {
      toast.error('Challans already created from this manifest');
      return;
    }
    setCreatingChallans(true);
    try {
      const res = await dispatchAPI.createChallansFromManifest(selectedManifest.id);
      toast.success(`Created ${res.data.challans?.length || 0} dispatch challans`);
      const updatedRes = await dispatchAPI.getManifest(selectedManifest.id);
      setSelectedManifest(updatedRes.data);
      loadManifests();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create challans');
    } finally {
      setCreatingChallans(false);
    }
  };

  const deleteManifest = async (manifestId) => {
    if (!window.confirm('Delete this manifest?')) return;
    try {
      await dispatchAPI.deleteManifest(manifestId);
      toast.success('Manifest deleted');
      setViewManifestOpen(false);
      loadManifests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // ================= CHALLAN FUNCTIONS =================

  const openCreate = async () => {
    try {
      const [custRes, itemRes, godownRes, stockRes] = await Promise.all([
        contractsAPI.activeCustomers(),
        inventoryAPI.listItems({ limit: 500 }),
        inventoryAPI.listGodowns(),
        inventoryAPI.getStock(),
      ]);
      setCustomers(custRes.data || []);
      setItems(itemRes.data.items || []);
      setGodowns(godownRes.data || []);
      setStock(stockRes.data || []);
      setDcForm({ customer_id: '', dispatch_type: 'delivery_challan', invoice_number: '', remarks: '', inbound_challan_id: '' });
      setDcItems([{ item_id: '', quantity: '', godown_id: '', remarks: '' }]);
      setOpenInboundChallans([]);
      setCreateOpen(true);
    } catch { toast.error('Failed to load data'); }
  };

  const addItem = () => {
    setDcItems(prev => [...prev, { item_id: '', quantity: '', godown_id: '', remarks: '' }]);
  };

  const removeItem = (idx) => {
    if (dcItems.length <= 1) return;
    setDcItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx, field, value) => {
    setDcItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const getAvailableStock = (itemId, godownId) => {
    const rec = stock.find(s => s.item_id === itemId && s.godown_id === godownId);
    return rec ? rec.quantity : 0;
  };

  const onCustomerSelect = async (customerId) => {
    setDcForm(f => ({ ...f, customer_id: customerId, inbound_challan_id: '' }));
    setOpenInboundChallans([]);
    if (customerId) {
      try {
        const res = await subcontractAPI.getOpenInboundChallans(customerId);
        setOpenInboundChallans(res.data || []);
      } catch { /* ignore */ }
    }
  };

  const onInboundChallanSelect = async (challanId) => {
    setDcForm(f => ({ ...f, inbound_challan_id: challanId === 'none' ? '' : challanId }));
    if (challanId && challanId !== 'none') {
      try {
        const res = await subcontractAPI.getExpectedOutputItems(challanId);
        const outputItems = res.data || [];
        if (outputItems.length > 0) {
          // Filter item options to only show expected output items
          const outputItemIds = new Set(outputItems.map(o => o.item_id));
          const filtered = items.filter(i => outputItemIds.has(i.id));
          setItems(filtered.length > 0 ? filtered : items);

          // Auto-populate dispatch items with FG items that have stock in FG godown
          const fgGodown = godowns.find(g => g.code === 'FG' || g.name?.toLowerCase().includes('finished'));
          if (fgGodown) {
            const newDcItems = [];
            for (const oi of outputItems) {
              const availQty = getAvailableStock(oi.item_id, fgGodown.id);
              if (availQty > 0) {
                newDcItems.push({
                  item_id: oi.item_id,
                  quantity: String(Math.min(availQty, oi.quantity || availQty)),
                  godown_id: fgGodown.id,
                });
              }
            }
            if (newDcItems.length > 0) {
              setDcItems(newDcItems);
              return;
            }
          }
        }
      } catch { /* ignore - keep all items */ }
    } else {
      // Reset to all items when no challan selected
      try {
        const res = await inventoryAPI.listItems({ limit: 500 });
        setItems(res.data.items || []);
      } catch { /* ignore */ }
    }
    setDcItems([{ item_id: '', quantity: '', godown_id: '' }]);
  };

  const handleCreate = async () => {
    if (!dcForm.customer_id) { toast.error('Select a customer'); return; }
    const validItems = dcItems.filter(i => i.item_id && parseFloat(i.quantity) > 0 && i.godown_id);
    if (validItems.length === 0) { toast.error('Add at least one item with quantity and godown'); return; }

    for (const item of validItems) {
      const avail = getAvailableStock(item.item_id, item.godown_id);
      if (avail < parseFloat(item.quantity)) {
        const itemName = items.find(i => i.id === item.item_id)?.item_name || item.item_id;
        toast.error(`Insufficient stock for ${itemName}. Available: ${avail}`);
        return;
      }
    }

    setCreating(true);
    try {
      const payload = {
        ...dcForm,
        items: validItems.map(i => ({
          item_id: i.item_id,
          quantity: parseFloat(i.quantity),
          godown_id: i.godown_id,
          remarks: i.remarks,
        })),
      };
      const res = await dispatchAPI.createChallan(payload);
      toast.success(`Dispatch ${res.data.dc_number} created`);
      setCreateOpen(false);
      loadChallans();
      loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create dispatch'); }
    finally { setCreating(false); }
  };

  const downloadPDF = async (challanId, dcNumber) => {
    try {
      toast.info('Generating PDF...');
      const res = await dispatchAPI.downloadPDF(challanId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DC_${dcNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
  };

  const openReconcile = (challan) => {
    setReconcileChallan(challan);
    setReconcileForm({ invoice_number: '', reconciliation_type: 'invoice', remarks: '' });
    setInvoiceFile(null);
    setReconcileOpen(true);
  };

  const handleReconcile = async () => {
    if (!reconcileChallan) return;
    if ((reconcileForm.reconciliation_type === 'invoice' || reconcileForm.reconciliation_type === 'rejection_invoice') && !reconcileForm.invoice_number) {
      toast.error('Enter invoice number');
      return;
    }

    try {
      if (invoiceFile) {
        setUploadingInvoice(true);
        const formData = new FormData();
        formData.append('file', invoiceFile);
        await dispatchAPI.uploadInvoice(reconcileChallan.id, formData);
      }

      await dispatchAPI.reconcileChallan(reconcileChallan.id, reconcileForm);
      toast.success('Challan reconciled');
      setReconcileOpen(false);
      loadChallans();
      loadDashboard();
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to reconcile'); 
    } finally {
      setUploadingInvoice(false);
    }
  };

  const downloadInvoiceAttachment = async (challan) => {
    try {
      const res = await dispatchAPI.downloadInvoice(challan.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = challan.invoice_attachment_name || `Invoice_${challan.dc_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch { toast.error('Failed to download invoice'); }
  };

  // COC generation — opens a dialog (Quality Manager / Admin only) that lets the
  // user pick which DC line, override X7 (RM Lot) / X10 (Customer PO), and choose
  // whether to also attach the generated DOCX as a permanent record on the DC.
  const [cocDialogOpen, setCocDialogOpen] = useState(false);
  const [cocDialogChallan, setCocDialogChallan] = useState(null);
  const [cocItemIndex, setCocItemIndex] = useState(0);
  const [cocRmLot, setCocRmLot] = useState('');
  const [cocCustomerPo, setCocCustomerPo] = useState('');
  const [cocQtyDispatched, setCocQtyDispatched] = useState('');
  const [cocQtyAccepted, setCocQtyAccepted] = useState('');
  const [cocAttach, setCocAttach] = useState(false);
  const [cocFormat, setCocFormat] = useState('pdf'); // pdf (default) | docx
  const [cocBusy, setCocBusy] = useState(false);

  const seedQtyFromLine = (challan, idx) => {
    const it = (challan?.items || [])[idx];
    if (!it) return;
    const dispQty = it.quantity != null ? String(it.quantity) : '';
    const accQty = it.accepted_quantity != null ? String(it.accepted_quantity) : dispQty;
    setCocQtyDispatched(dispQty);
    setCocQtyAccepted(accQty);
  };

  const openCocDialog = (challan) => {
    if (!canIssueCoc) return;
    setCocDialogChallan(challan);
    setCocItemIndex(0);
    setCocRmLot('');
    setCocCustomerPo('');
    setCocAttach(false);
    setCocFormat('pdf');
    seedQtyFromLine(challan, 0);
    setCocDialogOpen(true);
  };

  const generateCocNow = async () => {
    if (!cocDialogChallan) return;
    const items = cocDialogChallan.items || [];
    if (!items.length) { toast.error('This DC has no items'); return; }
    const idx = Math.min(Math.max(cocItemIndex, 0), items.length - 1);
    setCocBusy(true);
    try {
      const res = await dispatchAPI.downloadCOC(cocDialogChallan.id, {
        itemIndex: idx,
        rmLotOverride: cocRmLot.trim() || undefined,
        customerPoOverride: cocCustomerPo.trim() || undefined,
        qtyDispatchedOverride: cocQtyDispatched === '' ? undefined : cocQtyDispatched,
        qtyAcceptedOverride: cocQtyAccepted === '' ? undefined : cocQtyAccepted,
        attach: cocAttach,
        fmt: cocFormat,
      });
      // Determine the actual format returned (server may fall back to DOCX if PDF converter is missing)
      const returnedFmt = (res.headers && (res.headers['x-coc-format'] || res.headers['X-COC-Format'])) || cocFormat;
      const returnedType = (res.headers && res.headers['content-type']) || (returnedFmt === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      const pdfFallback = res.headers && (res.headers['x-coc-pdf-fallback'] || res.headers['X-COC-PDF-Fallback']);
      const blob = new Blob([res.data], { type: returnedType });
      const url = window.URL.createObjectURL(blob);
      const unique = items[idx].item_unique_id || items[idx].item_id || 'item';
      const filename = `COC_${cocDialogChallan.dc_number}_${unique}.${returnedFmt}`;
      // Open in a new tab so the current page stays intact. PDFs render
      // inline in the new tab; DOCX files trigger a download because the
      // browser cannot render that MIME type.
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Pop-up blocker fallback — synthesise a hidden anchor click.
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      // Defer revocation so the new tab/window has time to start the download.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);

      if (pdfFallback) {
        toast.warning('PDF converter unavailable on server — downloaded as DOCX instead');
      } else if (cocAttach) {
        toast.success(`COC generated (${returnedFmt.toUpperCase()}) and attached to DC`);
        loadChallans();
      } else {
        toast.success(`COC generated (${returnedFmt.toUpperCase()})`);
      }
      setCocDialogOpen(false);
    } catch (err) {
      let msg = 'Failed to generate COC';
      if (err?.response?.data) {
        try { const txt = await err.response.data.text(); const p = JSON.parse(txt); msg = p.detail || msg; } catch { /* ignore */ }
      }
      toast.error(msg);
    } finally {
      setCocBusy(false);
    }
  };

  // Bundle (ZIP): one COC per DC line (= per trace id / customer WO#).
  const generateCocBundleNow = async () => {
    if (!cocDialogChallan) return;
    const items = cocDialogChallan.items || [];
    if (!items.length) { toast.error('This DC has no items'); return; }
    setCocBusy(true);
    try {
      const res = await dispatchAPI.downloadCOCBundle(cocDialogChallan.id, {
        fmt: cocFormat,
        attach: cocAttach,
      });
      const pdfFallback = res.headers && (res.headers['x-coc-pdf-fallback'] || res.headers['X-COC-PDF-Fallback']);
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const filename = `COC_Bundle_${cocDialogChallan.dc_number}.zip`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      if (pdfFallback) {
        toast.warning('PDF converter unavailable on server — ZIP contains DOCX files');
      } else if (cocAttach) {
        toast.success(`COC bundle generated (${items.length} certificates) and attached to DC`);
        loadChallans();
      } else {
        toast.success(`COC bundle generated — ${items.length} certificates`);
      }
      setCocDialogOpen(false);
    } catch (err) {
      let msg = 'Failed to generate COC bundle';
      if (err?.response?.data) {
        try { const txt = await err.response.data.text(); const p = JSON.parse(txt); msg = p.detail || msg; } catch { /* ignore */ }
      }
      toast.error(msg);
    } finally {
      setCocBusy(false);
    }
  };

  // Replace the invoice attached to a dispatch challan (including reconciled ones).
  // Reconciliation metadata (invoice number, amount, type) is preserved — only the PDF/image file is swapped.
  const replaceInvoiceInputRef = useRef(null);
  const [replaceInvoiceChallanId, setReplaceInvoiceChallanId] = useState(null);
  const [replacingInvoice, setReplacingInvoice] = useState(false);

  const handleReplaceInvoiceClick = (c) => {
    setReplaceInvoiceChallanId(c.id);
    setTimeout(() => replaceInvoiceInputRef.current?.click(), 0);
  };

  const handleReplaceInvoiceChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const cid = replaceInvoiceChallanId;
    if (!file || !cid) { setReplaceInvoiceChallanId(null); return; }
    if (!['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Only PDF or JPG/PNG files are allowed');
      setReplaceInvoiceChallanId(null);
      return;
    }
    if (!window.confirm('Replace the attached invoice file? The existing file will be overwritten. Reconciliation details (invoice number, amount, type) are kept intact.')) {
      setReplaceInvoiceChallanId(null);
      return;
    }
    setReplacingInvoice(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await dispatchAPI.uploadInvoice(cid, fd);
      toast.success('Invoice file replaced');
      loadChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to replace invoice');
    } finally {
      setReplacingInvoice(false);
      setReplaceInvoiceChallanId(null);
    }
  };

  // ================= ONE-CLICK DISPATCH =================
  const openOneClickDispatch = async () => {
    if (!selectedManifest) return;
    const cust = customers.find(c => c.id === selectedManifest.customer_id);
    // Auto-populate TO from customer + all programs whose items are in this manifest
    let autoTo = cust?.contact_email || cust?.email || '';
    try {
      const r = await dispatchAPI.getManifestRecipients(selectedManifest.id);
      const emails = r.data?.to || [];
      if (emails.length) autoTo = emails.join(', ');
    } catch { /* fallback to customer email */ }
    setOneClickForm({
      to_email: autoTo,
      cc_email: '',
      subject: `Dispatch Confirmation — ${selectedManifest.customer_name || ''} — ${selectedManifest.manifest_number || ''}`,
      message: '',
      courier_name: '',
      tracking_number: '',
    });
    setOneClickOpen(true);
  };

  const handleOneClickDispatch = async () => {
    if (!selectedManifest) return;
    if (!oneClickForm.to_email) { toast.error('Enter customer email'); return; }
    setOneClickSending(true);
    try {
      const res = await dispatchAPI.oneClickDispatch(selectedManifest.id, oneClickForm);
      toast.success(res.data?.message || 'Dispatch sent');
      setOneClickOpen(false);
      await loadManifests();
      if (selectedManifest) {
        const fresh = await dispatchAPI.getManifest(selectedManifest.id);
        setSelectedManifest(fresh.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'One-click dispatch failed');
    } finally {
      setOneClickSending(false);
    }
  };

  // ================= CUSTOMER EMAIL FUNCTIONS =================
  const openCustomerEmailDialog = async () => {
    setEmailCustomerId('');
    setEmailChallans([]);
    setSelectedEmailChallans([]);
    setShowAlreadyEmailed(false);
    setShowOldUnsent(false);
    setIncludeNonInvoiced(false);
    setIncludeNonReconciled(false);
    setCustomerEmailForm({
      to_email: '',
      cc_email: '',
      subject: '',
      message: '',
      courier_name: '',
      tracking_number: '',
      include_challan_pdf: true,
      include_coc_bundle: false,
    });
    setCustomerEmailOpen(true);
  };

  const onEmailCustomerSelect = async (customerId) => {
    setEmailCustomerId(customerId);
    setSelectedEmailChallans([]);
    setEmailChallans([]);
    if (customerId) {
      await loadEmailChallans(customerId, { nonInvoiced: includeNonInvoiced, nonReconciled: includeNonReconciled });
      // Pre-populate customer email from customer + all of that customer's programs
      const customer = customers.find(c => c.id === customerId);
      const emails = [];
      const custEmailRaw = customer?.contact_email || customer?.email || '';
      custEmailRaw.split(',').forEach(e => {
        const t = e.trim();
        if (t && !emails.some(x => x.toLowerCase() === t.toLowerCase())) emails.push(t);
      });
      try {
        const pr = await programsAPI.list({ customer_id: customerId });
        (pr.data?.programs || pr.data || []).forEach(p => {
          (p.email_recipients || '').split(',').forEach(e => {
            const t = e.trim();
            if (t && !emails.some(x => x.toLowerCase() === t.toLowerCase())) emails.push(t);
          });
        });
      } catch { /* programs fetch optional */ }
      if (emails.length) {
        setCustomerEmailForm(f => ({ ...f, to_email: emails.join(', ') }));
      }
    }
  };

  // A DC is "non-invoiced" (FOC / return / return-as-is) when it will never carry
  // a sale invoice: return & return-as-is categories, or a delivery DC whose lines
  // are all rate 0 (e.g. WO-finished job-work returned to customer without invoicing).
  const isNonInvoicedChallan = (c) => {
    if (c.reconciliation_status === 'reconciled') return false; // already surfaced
    if (c.challan_category === 'return' || c.challan_category === 'return_as_is') return true;
    if (c.reconciliation_type === 'foc') return true;
    const items = c.items || [];
    return items.length > 0 && items.every(it => (parseFloat(it.rate) || 0) <= 0);
  };

  const loadEmailChallans = async (customerId, opts = {}) => {
    const { nonInvoiced = false, nonReconciled = false } = opts;
    try {
      // Base eligible challans: reconciled delivery DCs (regular sales) + return DCs.
      const [delRes, retRes] = await Promise.all([
        dispatchAPI.listChallans({ customer_id: customerId, reconciliation_status: 'reconciled', limit: 200 }),
        dispatchAPI.listChallans({ customer_id: customerId, challan_category: 'return', limit: 200 }),
      ]);
      const delivered = delRes.data.challans || [];
      const returns = retRes.data.challans || [];
      const byId = new Map();
      [...delivered, ...returns].forEach(c => byId.set(c.id, c));

      // Optionally pull in more DCs the base query hides:
      //   - nonReconciled: EVERY non-cancelled DC (incl. pending saleable ones)
      //   - nonInvoiced:   only FOC / Return-as-is DCs (rate-0 lines / return categories)
      if (nonInvoiced || nonReconciled) {
        const allRes = await dispatchAPI.listChallans({ customer_id: customerId, limit: 500 });
        (allRes.data.challans || []).forEach(c => {
          if (byId.has(c.id)) return;
          if (nonReconciled && c.reconciliation_status !== 'reconciled') byId.set(c.id, c);
          else if (nonInvoiced && isNonInvoicedChallan(c)) byId.set(c.id, c);
        });
      }

      const merged = Array.from(byId.values());
      merged.sort((a, b) => (b.dispatch_date || '').localeCompare(a.dispatch_date || ''));
      setEmailChallans(merged);
    } catch { /* ignore */ }
  };

  const toggleEmailChallan = (challanId) => {
    setSelectedEmailChallans(prev => {
      if (prev.includes(challanId)) {
        return prev.filter(id => id !== challanId);
      } else {
        return [...prev, challanId];
      }
    });
  };

  const handleSendCustomerEmail = async () => {
    if (!emailCustomerId) {
      toast.error('Select a customer');
      return;
    }
    if (selectedEmailChallans.length === 0) {
      toast.error('Select at least one challan');
      return;
    }
    if (!customerEmailForm.to_email) {
      toast.error('Enter recipient email');
      return;
    }

    setSendingCustomerEmail(true);
    try {
      const res = await dispatchAPI.emailToCustomer({
        customer_id: emailCustomerId,
        challan_ids: selectedEmailChallans,
        to_email: customerEmailForm.to_email,
        cc_email: customerEmailForm.cc_email,
        subject: customerEmailForm.subject,
        message: customerEmailForm.message,
        courier_name: customerEmailForm.courier_name,
        tracking_number: customerEmailForm.tracking_number,
        include_challan_pdf: customerEmailForm.include_challan_pdf,
        include_coc_bundle: customerEmailForm.include_coc_bundle,
      });
      const cocCount = res?.data?.coc_attached_count || 0;
      toast.success(
        cocCount > 0
          ? `Dispatch email sent — ${cocCount} COC(s) attached`
          : 'Dispatch email sent to customer successfully'
      );
      setCustomerEmailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingCustomerEmail(false);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const manifestTotalPages = Math.ceil(manifestTotal / limit);
  const scrapTotalPages = Math.ceil(scrapTotal / limit);

  const itemOptions = items
    .filter(i => includeFixedAssets || i.item_type !== 'Fixed Asset')
    .map(i => ({
      value: i.id,
      label: `${i.unique_id} - ${i.item_name}`,
      searchStr: `${i.unique_id} ${i.item_name} ${i.item_type || ''}`,
    }));

  const godownOptions = godowns.map(g => ({
    value: g.id,
    label: g.name,
  }));

  const customerOptions = customers.map(c => ({
    value: c.id,
    label: c.customer_name,
    sub: c.gst_number || '',
    searchStr: `${c.customer_name} ${c.gst_number || ''}`,
  }));

  const getManifestStatusBadge = (status) => {
    const statusMap = {
      draft: { variant: 'secondary', label: 'Draft' },
      emailed: { variant: 'default', label: 'Emailed' },
      challans_created: { variant: 'outline', label: 'Challans Created' },
    };
    const s = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge variant={s.variant} className="text-[10px]">{s.label}</Badge>;
  };

  const selectedScrapWeight = customerPendingScrap
    .filter(e => selectedScrapEntries.includes(e.id))
    .reduce((sum, e) => sum + (e.actual_weight || 0), 0);

  return (
    <div className="space-y-4">
      {/* Hidden input used by the "Replace Invoice" action on the DC list */}
      <input
        type="file"
        accept="application/pdf,image/jpeg,image/jpg,image/png"
        ref={replaceInvoiceInputRef}
        className="hidden"
        onChange={handleReplaceInvoiceChosen}
        data-testid="replace-invoice-file-input"
      />

      {/* COC generation dialog (Quality Manager / Admin only) */}
      <Dialog open={cocDialogOpen} onOpenChange={setCocDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="coc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-violet-600" />
              Generate Certificate of Conformance
            </DialogTitle>
          </DialogHeader>
          {cocDialogChallan && (
            <div className="space-y-3 text-xs">
              <div className="rounded border bg-slate-50 px-3 py-2">
                <div className="font-medium">{cocDialogChallan.dc_number}</div>
                <div className="text-[11px] text-muted-foreground">{cocDialogChallan.customer_name}</div>
              </div>

              {(cocDialogChallan.items || []).length >= 1 && (
                <div>
                  <Label className="text-xs">
                    Dispatch line to certify
                    <span className="text-muted-foreground"> — one COC per trace-id / customer WO#</span>
                  </Label>
                  <select
                    className="mt-1 w-full h-8 text-xs border rounded px-2 bg-background"
                    value={cocItemIndex}
                    onChange={e => {
                      const i = parseInt(e.target.value, 10);
                      setCocItemIndex(i);
                      seedQtyFromLine(cocDialogChallan, i);
                    }}
                    data-testid="coc-item-select"
                  >
                    {(cocDialogChallan.items || []).map((it, i) => {
                      const trace = (it.traceability_id || it.customer_wo_number || '').trim();
                      const traceTxt = trace ? `WO# ${trace}` : 'No WO#';
                      return (
                        <option key={i} value={i}>
                          {i + 1}. {(it.item_unique_id || '').trim()} — {it.item_name} · {traceTxt} · qty {it.quantity}
                        </option>
                      );
                    })}
                  </select>
                  {(cocDialogChallan.items || []).length > 1 && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Tip — use "Generate All (ZIP)" below to issue one COC per line in a single click.
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs">X7 — RM Lot / Batch Number <span className="text-muted-foreground">(optional override)</span></Label>
                <Input
                  value={cocRmLot}
                  onChange={e => setCocRmLot(e.target.value)}
                  placeholder="Leave blank to auto-resolve from inbound challan / purchase receipt"
                  className="h-7 text-xs"
                  data-testid="coc-rm-lot-input"
                />
              </div>

              <div>
                <Label className="text-xs">X10 — Customer PO Number <span className="text-muted-foreground">(optional override)</span></Label>
                <Input
                  value={cocCustomerPo}
                  onChange={e => setCocCustomerPo(e.target.value)}
                  placeholder="Leave blank to auto-resolve from challan / WO"
                  className="h-7 text-xs"
                  data-testid="coc-po-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">X11 — Quantity Dispatched</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={cocQtyDispatched}
                    onChange={e => setCocQtyDispatched(e.target.value)}
                    placeholder="From DC line"
                    className="h-7 text-xs"
                    data-testid="coc-qty-dispatched-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">X12 — Quantity Accepted</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={cocQtyAccepted}
                    onChange={e => setCocQtyAccepted(e.target.value)}
                    placeholder="Defaults to dispatched"
                    className="h-7 text-xs"
                    data-testid="coc-qty-accepted-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Output Format</Label>
                <select
                  className="mt-1 w-full h-8 text-xs border rounded px-2 bg-background"
                  value={cocFormat}
                  onChange={e => setCocFormat(e.target.value)}
                  data-testid="coc-format-select"
                >
                  <option value="pdf">PDF (recommended — inline-aligned, 1 page)</option>
                  <option value="docx">Word DOCX</option>
                </select>
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="coc-attach-label">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-violet-600"
                  checked={cocAttach}
                  onChange={e => setCocAttach(e.target.checked)}
                  data-testid="coc-attach-checkbox"
                />
                Also attach a permanent copy to this DC
              </label>

              <p className="text-[10px] text-muted-foreground">
                The COC issuance is audit-logged. If you attach a permanent copy, you can redownload it later from the DC record.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setCocDialogOpen(false)} disabled={cocBusy}>Cancel</Button>
            {cocDialogChallan && (cocDialogChallan.items || []).length > 1 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={generateCocBundleNow}
                disabled={cocBusy}
                data-testid="coc-generate-bundle-btn"
                title="Generate one COC per dispatch line and download as a ZIP"
              >
                {cocBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                All lines (ZIP)
              </Button>
            )}
            <Button size="sm" onClick={generateCocNow} disabled={cocBusy} data-testid="coc-generate-btn">
              {cocBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileCheck className="w-3 h-3 mr-1" />}
              {cocBusy ? 'Generating…' : 'Generate this line'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Dispatch Management</h1>
          <p className="text-xs text-muted-foreground">Manifests, delivery challans, and scrap returns</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={openCustomerEmailDialog} data-testid="email-customer-btn">
            <Mail className="w-3.5 h-3.5 mr-1" /> Email Customer
          </Button>
          <Button size="sm" variant="outline" onClick={openCreateManifest} data-testid="create-manifest-btn">
            <ClipboardList className="w-3.5 h-3.5 mr-1" /> New Manifest
          </Button>
          <Button size="sm" variant="outline" onClick={openCreateScrapReturn} data-testid="create-scrap-return-btn">
            <Recycle className="w-3.5 h-3.5 mr-1" /> Scrap Return
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="create-dispatch-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Dispatch
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-blue-500" />
            <div>
              <p className="text-[10px] text-muted-foreground">Total Dispatches</p>
              <p className="text-lg font-bold">{stats.total_challans || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-amber-500" />
            <div>
              <p className="text-[10px] text-muted-foreground">Pending Reconciliation</p>
              <p className="text-lg font-bold">{stats.pending_reconciliation || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-green-500" />
            <div>
              <p className="text-[10px] text-muted-foreground">Dispatched Today</p>
              <p className="text-lg font-bold">{stats.dispatched_today || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <div>
              <p className="text-[10px] text-muted-foreground">Supplier Returns Pending</p>
              <p className="text-lg font-bold">{stats.supplier_return_pending || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Recycle className="w-4 h-4 text-orange-500" />
            <div>
              <p className="text-[10px] text-muted-foreground">Scrap in Godown</p>
              <p className="text-lg font-bold">{scrapStats.total_scrap_in_godown || 0} kg</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="plan" data-testid="tab-dispatch-plan">Dispatch Plan</TabsTrigger>
          <TabsTrigger value="quick" data-testid="tab-quick-dispatch">Quick Dispatch</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="manifests" data-testid="tab-manifests">Manifests</TabsTrigger>
          <TabsTrigger value="challans" data-testid="tab-challans">Delivery Challans</TabsTrigger>
          <TabsTrigger value="supplier_returns" data-testid="tab-supplier-returns">Supplier Returns</TabsTrigger>
          <TabsTrigger value="scrap" data-testid="tab-scrap">Scrap Returns</TabsTrigger>
        </TabsList>

        {/* Dispatch Plan Tab — auto work-order explosion */}
        <TabsContent value="plan">
          <DispatchPlanner />
        </TabsContent>

        {/* Quick Dispatch Tab */}
        <TabsContent value="quick">
          <QuickDispatch />
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <DispatchDashboard stats={stats}
            onGoToManifests={() => setActiveTab('manifests')}
            onGoToChallans={(filter) => {
              setActiveTab('challans');
              if (filter?.email) setEmailFilter(filter.email);
              if (filter?.reconciliation) setStatusFilter(filter.reconciliation);
            }} />
        </TabsContent>

        {/* Manifests Tab */}
        <TabsContent value="manifests">
          <Card>
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Dispatch Manifests</CardTitle>
                <Select value={manifestStatusFilter} onValueChange={v => { setManifestStatusFilter(v); setManifestPage(1); }}>
                  <SelectTrigger className="w-40 h-7 text-xs" data-testid="manifest-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="emailed">Emailed</SelectItem>
                    <SelectItem value="challans_created">Challans Created</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
              ) : manifests.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No manifests found</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-2.5 text-left font-medium">Manifest No</th>
                          <th className="p-2.5 text-left font-medium">Customer</th>
                          <th className="p-2.5 text-left font-medium">Items</th>
                          <th className="p-2.5 text-left font-medium">Created</th>
                          <th className="p-2.5 text-left font-medium">Status</th>
                          <th className="p-2.5 text-center font-medium">Email</th>
                          <th className="p-2.5 text-right font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {manifests.map(m => (
                          <tr key={m.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => viewManifest(m.id)}>
                            <td className="p-2.5 font-mono text-xs">{m.manifest_number}</td>
                            <td className="p-2.5">{m.customer_name}</td>
                            <td className="p-2.5">{m.total_items} ({m.total_quantity} qty)</td>
                            <td className="p-2.5">{m.created_at?.slice(0, 10)}</td>
                            <td className="p-2.5">{getManifestStatusBadge(m.status)}</td>
                            <td className="p-2.5 text-center">
                              {m.emailed_at ? (
                                <div className="flex items-center justify-center gap-1">
                                  <span className="text-[10px] inline-block px-1.5 py-0.5 rounded border border-blue-300 text-blue-700 bg-blue-50"
                                    title={`Last sent ${m.emailed_at?.slice(0, 16).replace('T', ' ')} to ${m.emailed_to || ''}`}>
                                    Sent {m.emailed_at?.slice(0, 10)}
                                  </span>
                                  {(m.manifest_email_count || 0) > 1 && (
                                    <span className="text-[9px] inline-block px-1 py-0 rounded border border-amber-300 text-amber-700 bg-amber-50"
                                      title={(m.manifest_email_history || []).map(h => `${(h.at || '').slice(0, 16).replace('T', ' ')} → ${h.to || ''}`).join('\n')}
                                      data-testid={`manifest-resent-${m.manifest_number}`}>
                                      ×{m.manifest_email_count}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[10px] text-rose-600" title="Not yet emailed to accounts">Not sent</span>
                              )}
                            </td>
                            <td className="p-2.5 text-right" onClick={e => e.stopPropagation()}>
                              <div className="flex gap-1 justify-end">
                                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => viewManifest(m.id)} title="View">
                                  <Eye className="w-3 h-3" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => downloadManifestPDF(m.id, m.manifest_number)} title="Download PDF">
                                  <Download className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {manifestTotalPages > 1 && (
                    <div className="flex items-center justify-between p-3 border-t">
                      <span className="text-xs text-muted-foreground">Page {manifestPage} of {manifestTotalPages}</span>
                      <div className="flex gap-1">
                        <Button size="icon" variant="outline" className="h-7 w-7" disabled={manifestPage === 1} onClick={() => setManifestPage(p => p - 1)}>
                          <ChevronLeft className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="outline" className="h-7 w-7" disabled={manifestPage === manifestTotalPages} onClick={() => setManifestPage(p => p + 1)}>
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Challans Tab */}
        <TabsContent value="challans">
          <Card>
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Delivery Challans</CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={emailFilter} onValueChange={v => { setEmailFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-36 h-7 text-xs" data-testid="dispatch-email-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_sent">Not Emailed</SelectItem>
                      <SelectItem value="sent">Emailed</SelectItem>
                      <SelectItem value="all">All (Email)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-40 h-7 text-xs" data-testid="dispatch-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending Reconciliation</SelectItem>
                      <SelectItem value="reconciled">Reconciled</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="h-7 text-[10px]"
                    onClick={async () => {
                      try {
                        const res = await dispatchAPI.backfillCustomerInfo();
                        toast.success(`${res.data.message}`);
                        loadChallans();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                    }}
                    data-testid="backfill-customer-info-btn"
                  >
                    <Wrench className="w-3 h-3 mr-1" /> Fix Legacy DCs
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
              ) : challans.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No dispatches found</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-2.5 text-left font-medium">DC Number</th>
                          <th className="p-2.5 text-left font-medium">Cust DC#</th>
                          <th className="p-2.5 text-left font-medium">Cust PO#</th>
                          <th className="p-2.5 text-left font-medium">Customer</th>
                          <th className="p-2.5 text-left font-medium">Type</th>
                          <th className="p-2.5 text-left font-medium">Items</th>
                          <th className="p-2.5 text-left font-medium">Date</th>
                          <th className="p-2.5 text-left font-medium">Status</th>
                          <th className="p-2.5 text-center font-medium">Emailed</th>
                          <th className="p-2.5 text-right font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {challans.map(c => (
                          <tr key={c.id} className={`hover:bg-muted/30 ${c.status === 'cancelled' ? 'opacity-50 bg-muted/40' : (c.challan_category === 'return' ? 'bg-red-50/30' : '')}`}>
                            <td className="p-2.5 font-mono text-xs">
                              {c.dc_number}
                              {c.status === 'cancelled' && (
                                <Badge variant="outline" className="ml-1 text-[8px] px-1 py-0 border-red-300 text-red-600" data-testid={`cancelled-badge-${c.dc_number}`}>Cancelled</Badge>
                              )}
                            </td>
                            <td className="p-2.5 text-[10px] font-mono">{c.customer_challan_number || '-'}</td>
                            <td className="p-2.5 text-[10px] font-mono">{c.customer_po_number || c.po_numbers || '-'}</td>
                            <td className="p-2.5">{c.customer_name}</td>
                            <td className="p-2.5">
                              {c.challan_category === 'return' ? (
                                <Badge variant="destructive" className="text-[9px] px-1.5 py-0">
                                  <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Return
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-green-300 text-green-700 bg-green-50">
                                  Delivery
                                </Badge>
                              )}
                            </td>
                            <td className="p-2.5">{c.total_items} item(s)</td>
                            <td className="p-2.5">{c.dispatch_date?.slice(0, 10)}</td>
                            <td className="p-2.5">
                              {c.reconciliation_status === 'not_applicable' ? (
                                <Badge variant="secondary" className="text-[10px]">N/A (Return)</Badge>
                              ) : (
                                <Badge variant={c.reconciliation_status === 'reconciled' ? 'default' : 'secondary'} className="text-[10px]">
                                  {c.reconciliation_status === 'reconciled' ? (c.reconciliation_type === 'foc' ? 'FOC' : c.invoice_number || 'Reconciled') : 'Pending'}
                                </Badge>
                              )}
                            </td>
                            <td className="p-2.5 text-center">
                              {c.customer_emailed_at ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-blue-300 text-blue-700 bg-blue-50" title={`Emailed ${c.customer_emailed_at?.slice(0,10)} to ${c.customer_emailed_to || ''}`}>
                                    <Mail className="w-2.5 h-2.5 mr-0.5" /> Sent
                                  </Badge>
                                  {(c.customer_email_count || 0) > 1 && (
                                    <Badge
                                      variant="outline"
                                      className="text-[9px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50"
                                      title={(c.customer_email_history || [])
                                        .map(h => `${(h.at || '').slice(0,16).replace('T',' ')} → ${h.to || ''}`)
                                        .join('\n')}
                                      data-testid={`reemailed-badge-${c.dc_number}`}
                                    >
                                      Re-emailed {c.customer_email_count - 1}x
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-2.5 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => downloadPDF(c.id, c.dc_number)} title="Download PDF" data-testid={`pdf-${c.dc_number}`}>
                                  <Download className="w-3 h-3" />
                                </Button>
                                {c.invoice_attachment_id && (
                                  <>
                                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => downloadInvoiceAttachment(c)} title="Download Invoice">
                                      <FileText className="w-3 h-3 text-green-600" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6"
                                      onClick={() => handleReplaceInvoiceClick(c)}
                                      disabled={replacingInvoice}
                                      title="Replace invoice file (e.g. wrong invoice uploaded)"
                                      data-testid={`replace-invoice-${c.dc_number}`}
                                    >
                                      {replacingInvoice && replaceInvoiceChallanId === c.id
                                        ? <Loader2 className="w-3 h-3 animate-spin text-amber-600" />
                                        : <RotateCcw className="w-3 h-3 text-amber-600" />}
                                    </Button>
                                  </>
                                )}
                                {/* COC download — one per item on the DC — Quality Manager / Admin only */}
                                {canIssueCoc && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-6 w-6"
                                    onClick={() => openCocDialog(c)}
                                    title="Generate Certificate of Conformance"
                                    data-testid={`coc-${c.dc_number}`}
                                  >
                                    <FileCheck className="w-3 h-3 text-violet-600" />
                                  </Button>
                                )}
                                {c.reconciliation_status === 'pending' && c.challan_category !== 'return' && (
                                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => openReconcile(c)} data-testid={`reconcile-${c.dc_number}`}>
                                    <FileCheck className="w-3 h-3 mr-0.5" /> Reconcile
                                  </Button>
                                )}
                                {isAdmin && c.status !== 'cancelled' && c.reconciliation_status !== 'reconciled' && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-6 w-6"
                                    onClick={() => { setCancelChallanTarget(c); setCancelReason(''); }}
                                    title="Cancel challan & restore stock"
                                    data-testid={`cancel-challan-${c.dc_number}`}
                                  >
                                    <Ban className="w-3 h-3 text-red-600" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between p-3 border-t">
                      <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
                      <div className="flex gap-1">
                        <Button size="icon" variant="outline" className="h-7 w-7" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                          <ChevronLeft className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="outline" className="h-7 w-7" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Supplier Returns Tab */}
        <TabsContent value="supplier_returns">
          <Card>
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Return to Supplier (QC Rejected/Quarantined)</CardTitle>
                <Button size="sm" variant="outline" onClick={openSupplierReturnDialog} data-testid="create-supplier-return-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> New Supplier Return
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {supplierReturnChallans.length === 0 ? (
                <div className="p-8 text-center text-xs text-muted-foreground">
                  No supplier return challans found. Return quarantined/rejected items to suppliers using the button above.
                </div>
              ) : (
                <div className="divide-y">
                  {supplierReturnChallans.map(c => (
                    <div key={c.id} className="p-3 hover:bg-slate-50/50" data-testid={`supplier-return-challan-${c.dc_number}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm font-mono">{c.dc_number}</span>
                            <Badge variant="outline" className="text-[10px]">{c.supplier_name || 'Unknown Supplier'}</Badge>
                            <Badge variant="outline" className={`text-[10px] ${
                              c.reconciliation_status === 'reconciled' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                              {c.reconciliation_status === 'reconciled' ? 'Reconciled' : 'Pending Reconciliation'}
                            </Badge>
                            {c.reconciliation_type && (
                              <Badge variant="outline" className={`text-[10px] ${
                                c.reconciliation_type === 'rejection_invoice' ? 'bg-red-50 text-red-700 border-red-200' :
                                c.reconciliation_type === 'foc' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                'bg-slate-50 text-slate-700 border-slate-200'
                              }`}>
                                {c.reconciliation_type === 'rejection_invoice' ? 'Rejection Invoice' : c.reconciliation_type === 'foc' ? 'FOC' : c.reconciliation_type}
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                            <span>{c.total_items} item(s)</span>
                            <span>Dispatched: {c.dispatch_date ? new Date(c.dispatch_date).toLocaleDateString() : '-'}</span>
                            {c.invoice_number && <span>Invoice: {c.invoice_number}</span>}
                          </div>
                          {(c.items || []).map((item, idx) => (
                            <div key={idx} className="text-[10px] text-muted-foreground mt-0.5">
                              {item.item_name} — {item.quantity} {item.unit_of_measure || 'Nos'} from {item.godown_name}
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-1">
                          {c.reconciliation_status !== 'reconciled' && (
                            <Button size="sm" variant="ghost" onClick={() => openReconcile(c)} data-testid={`reconcile-supplier-return-${c.dc_number}`} className="h-7 text-xs">
                              <FileCheck className="w-3 h-3 mr-1" /> Reconcile
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => downloadPDF(c.id, c.dc_number)} className="h-7 text-xs">
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scrap Returns Tab */}
        <TabsContent value="scrap">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Pending Scrap by Customer */}
            <Card className="lg:col-span-1">
              <CardHeader className="py-3 px-4 border-b">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Scale className="w-4 h-4" /> Pending Scrap
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {pendingScrap.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground text-xs">No pending scrap</div>
                ) : (
                  <div className="divide-y max-h-[300px] overflow-y-auto">
                    {pendingScrap.map((group, idx) => (
                      <div key={idx} className="p-3">
                        <div className="flex justify-between items-center">
                          <p className="text-xs font-medium">{group.customer_name || 'No Customer'}</p>
                          <Badge variant="secondary" className="text-[10px]">
                            {group.total_weight?.toFixed(2) || 0} kg
                          </Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {group.entries?.length || 0} entries from work orders
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Scrap Returns List */}
            <Card className="lg:col-span-2">
              <CardHeader className="py-3 px-4 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Scrap Return Challans</CardTitle>
                  <Badge variant="outline" className="text-[10px]">
                    {scrapStats.monthly_returned?.toFixed(2) || 0} kg this month
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
                ) : scrapReturns.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground text-sm">No scrap returns yet</div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="p-2.5 text-left font-medium">Return No</th>
                            <th className="p-2.5 text-left font-medium">Customer</th>
                            <th className="p-2.5 text-left font-medium">Cust DC#</th>
                            <th className="p-2.5 text-left font-medium">Cust PO#</th>
                            <th className="p-2.5 text-left font-medium">Weight</th>
                            <th className="p-2.5 text-left font-medium">Date</th>
                            <th className="p-2.5 text-right font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {scrapReturns.map(sr => (
                            <tr key={sr.id} className="hover:bg-muted/30">
                              <td className="p-2.5 font-mono text-xs">{sr.return_number}</td>
                              <td className="p-2.5">{sr.customer_name}</td>
                              <td className="p-2.5 font-mono text-[10px]">{sr.customer_challan_number || '-'}</td>
                              <td className="p-2.5 font-mono text-[10px]" data-testid={`scrap-return-cust-po-${sr.id}`}>{sr.customer_po_number || '-'}</td>
                              <td className="p-2.5">{sr.total_weight?.toFixed(2)} kg</td>
                              <td className="p-2.5">{sr.created_at?.slice(0, 10)}</td>
                              <td className="p-2.5 text-right">
                                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => downloadScrapReturnPDF(sr.id, sr.return_number)} title="Download PDF">
                                  <Download className="w-3 h-3" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {scrapTotalPages > 1 && (
                      <div className="flex items-center justify-between p-3 border-t">
                        <span className="text-xs text-muted-foreground">Page {scrapPage} of {scrapTotalPages}</span>
                        <div className="flex gap-1">
                          <Button size="icon" variant="outline" className="h-7 w-7" disabled={scrapPage === 1} onClick={() => setScrapPage(p => p - 1)}>
                            <ChevronLeft className="w-3 h-3" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-7 w-7" disabled={scrapPage === scrapTotalPages} onClick={() => setScrapPage(p => p + 1)}>
                            <ChevronRight className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Manifest Dialog */}
      <Dialog open={createManifestOpen} onOpenChange={v => { setCreateManifestOpen(v); if (!v) { setPreviewStep(false); setPreviewItems([]); } }}>
        <DialogContent className={previewStep ? "max-w-3xl max-h-[80vh] overflow-y-auto" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle className="text-base">{previewStep ? 'Review Manifest Items' : 'Create Dispatch Manifest'}</DialogTitle>
          </DialogHeader>

          {!previewStep ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Customer *</Label>
                <SearchableSelect
                  options={customerOptions}
                  value={manifestCustomerId}
                  onSelect={setManifestCustomerId}
                  placeholder="Search customers..."
                  emptyText="No customers found."
                  testId="manifest-customer-select"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  System will auto-populate OK items (FG) and Not-OK items (Quarantine/Rejection) from open inbound challans
                </p>
              </div>

              {/* Item-type filter — defaults to Finished Good. Other types pulled from STORES godown and flagged FOC. */}
              <div>
                <Label className="text-xs">Include item types</Label>
                <div className="grid grid-cols-2 gap-1.5 mt-1.5" data-testid="manifest-item-type-filter">
                  {SELECTABLE_ITEM_TYPES.map(t => {
                    const checked = includeItemTypes.includes(t);
                    const isFG = t === 'Finished Good';
                    return (
                      <label
                        key={t}
                        className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer text-[11px] ${checked ? 'bg-blue-50 border-blue-300' : 'bg-background border-muted hover:bg-muted/30'}`}
                        data-testid={`item-type-${t.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setIncludeItemTypes(prev => {
                              const next = prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t];
                              // Always require at least one type selected
                              return next.length === 0 ? ['Finished Good'] : next;
                            });
                          }}
                          className="accent-blue-600"
                        />
                        <span>{t}</span>
                        {!isFG && checked && (
                          <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 ml-auto">FOC</span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Non-Finished-Good types come from the <strong>Stores</strong> godown only and are auto-flagged <strong>FOC</strong> (rate ₹0).
                </p>
              </div>

              {/* Optional: also include other godowns (e.g. Quarantine / Rejection) to return jobs to customer as FOC */}
              {(() => {
                const EXCLUDED = ['PROD', 'QC', 'SCRAP', 'REGRIND', 'STORE', 'FG', 'FINISHED'];
                const extraGodownOptions = (godowns || []).filter(g => !EXCLUDED.includes((g.code || '').toUpperCase()));
                if (extraGodownOptions.length === 0) return null;
                return (
                  <div>
                    <Label className="text-xs">Also include godowns (return to customer · FOC)</Label>
                    <div className="grid grid-cols-2 gap-1.5 mt-1.5" data-testid="manifest-extra-godown-filter">
                      {extraGodownOptions.map(g => {
                        const checked = extraGodownIds.includes(g.id);
                        return (
                          <label
                            key={g.id}
                            className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer text-[11px] ${checked ? 'bg-amber-50 border-amber-300' : 'bg-background border-muted hover:bg-muted/30'}`}
                            data-testid={`extra-godown-${(g.code || g.id).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setExtraGodownIds(prev => prev.includes(g.id) ? prev.filter(x => x !== g.id) : [...prev, g.id])}
                              className="accent-amber-600"
                            />
                            <span>{g.name}</span>
                            {checked && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 ml-auto">FOC</span>}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Only stock traceable to this customer is shown. These return as <strong>FOC</strong> (rate ₹0) and must be dispatched on a <strong>separate challan</strong> (no reconciliation).
                    </p>
                  </div>
                );
              })()}


              <div>
                <Label className="text-xs">Remarks</Label>
                <Textarea value={manifestRemarks} onChange={e => setManifestRemarks(e.target.value)}
                  className="text-xs min-h-[60px]" placeholder="Optional notes..." />
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setCreateManifestOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={handlePreviewManifest} disabled={previewLoading} data-testid="preview-manifest-btn">
                  {previewLoading ? 'Loading...' : 'Preview Items'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Drift warning — surfaced when inventory_balance disagrees with Σ trace_lots
                  for any (item, godown) in the preview. See ROOT_CAUSE.md.
                  Operators MUST reconcile before dispatching or some cust-WOs may be
                  silently excluded from the manifest lines. */}
              {driftWarnings.length > 0 && (
                <div className="border border-amber-300 bg-amber-50 rounded p-2 space-y-1.5" data-testid="manifest-drift-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 text-xs">
                      <div className="font-medium text-amber-900">Trace-lot drift detected — manifest lines may be incomplete</div>
                      <div className="text-amber-800 mt-0.5">
                        {driftWarnings.length} (item, godown) row(s) have inventory_balance ≠ Σ trace_lots.
                        Some customer WOs represented in stock may not appear below. Reconcile via
                        <b> Admin Tools → Auto-Fix Stale FG Lots</b> (or Manual Trace Lot Edit) before creating the DC.
                      </div>
                      <details className="mt-1">
                        <summary className="cursor-pointer text-amber-900 hover:underline">Show details ({driftWarnings.length})</summary>
                        <table className="w-full text-[10px] mt-1.5">
                          <thead className="text-amber-900/70">
                            <tr>
                              <th className="text-left font-normal">Item</th>
                              <th className="text-left font-normal">Godown</th>
                              <th className="text-right font-normal">Balance</th>
                              <th className="text-right font-normal">Σ Trace lots</th>
                              <th className="text-right font-normal">Drift</th>
                              <th className="text-left font-normal">Severity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {driftWarnings.map((w, i) => (
                              <tr key={i} className="border-t border-amber-200/50" data-testid={`drift-row-${i}`}>
                                <td className="py-0.5">{w.item_unique_id || w.item_id?.slice(0, 8)} — {w.item_name}</td>
                                <td>{w.godown_name}</td>
                                <td className="text-right font-mono">{w.balance}</td>
                                <td className="text-right font-mono">{w.trace_lots_total_available}</td>
                                <td className={`text-right font-mono ${w.drift < 0 ? 'text-red-700' : 'text-amber-700'}`}>{w.drift}</td>
                                <td>{w.severity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </details>
                    </div>
                  </div>
                </div>
              )}

              {/* Quality filter tabs - 3-way: Finished Goods / Rejection / Returned As Is */}
              {(() => {
                const hasFG = previewItems.some(i => i.quality_status === 'ok');
                const hasNotOk = previewItems.some(i => i.quality_status === 'not_ok');
                const hasReturnedAsIs = previewItems.some(i => i.quality_status === 'returned_as_is');
                const categoryCount = [hasFG, hasNotOk, hasReturnedAsIs].filter(Boolean).length;
                return categoryCount > 1 ? (
                  <div className="flex gap-1 p-1 bg-muted/30 rounded-md" data-testid="quality-filter-tabs">
                    <button
                      className={`px-3 py-1 text-xs rounded transition-colors ${!previewItems._qualityFilter || previewItems._qualityFilter === 'all' ? 'bg-background shadow text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                      onClick={() => setPreviewItems(prev => { prev._qualityFilter = 'all'; return [...prev]; })}
                    >All ({previewItems.length})</button>
                    {hasFG && (
                      <button
                        className={`px-3 py-1 text-xs rounded transition-colors flex items-center gap-1 ${previewItems._qualityFilter === 'ok' ? 'bg-green-100 text-green-800 font-medium shadow' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setPreviewItems(prev => { prev._qualityFilter = 'ok'; return [...prev]; })}
                        data-testid="quality-filter-ok"
                      ><ShieldCheck className="w-3 h-3" /> Finished Goods ({previewItems.filter(i => i.quality_status === 'ok').length})</button>
                    )}
                    {hasNotOk && (
                      <button
                        className={`px-3 py-1 text-xs rounded transition-colors flex items-center gap-1 ${previewItems._qualityFilter === 'not_ok' ? 'bg-red-100 text-red-800 font-medium shadow' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setPreviewItems(prev => { prev._qualityFilter = 'not_ok'; return [...prev]; })}
                        data-testid="quality-filter-notok"
                      ><AlertTriangle className="w-3 h-3" /> Rejection ({previewItems.filter(i => i.quality_status === 'not_ok').length})</button>
                    )}
                    {hasReturnedAsIs && (
                      <button
                        className={`px-3 py-1 text-xs rounded transition-colors flex items-center gap-1 ${previewItems._qualityFilter === 'returned_as_is' ? 'bg-amber-100 text-amber-800 font-medium shadow' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setPreviewItems(prev => { prev._qualityFilter = 'returned_as_is'; return [...prev]; })}
                        data-testid="quality-filter-returned"
                      ><RotateCcw className="w-3 h-3" /> Returned As Is ({previewItems.filter(i => i.quality_status === 'returned_as_is').length})</button>
                    )}
                  </div>
                ) : null;
              })()}

              <p className="text-xs text-muted-foreground">
                {previewItems.filter(i => i.selected).length} of {previewItems.length} items selected.
                Uncheck items you don't want to include. You can also edit quantities.
              </p>

              {previewItems.some(i => i.quality_status === 'not_ok') && (
                <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-md">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800">
                    <strong>Rejection items</strong> (from Quarantine/Rejection godowns) will create separate return challans. No invoice/email flow applies to these.
                  </p>
                </div>
              )}
              {previewItems.some(i => i.quality_status === 'returned_as_is') && (
                <div className="flex items-start gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-md">
                  <RotateCcw className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-blue-800">
                    <strong>Returned As Is</strong> items are raw materials from Store being returned to the customer without processing.
                  </p>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-2 w-8 text-center">
                        <input type="checkbox" className="rounded"
                          checked={previewItems.every(i => i.selected)}
                          onChange={e => setPreviewItems(prev => prev.map(i => ({ ...i, selected: e.target.checked })))}
                          data-testid="manifest-select-all"
                        />
                      </th>
                      <th className="p-2 text-left">Item</th>
                      <th className="p-2 text-center">Status</th>
                      <th className="p-2 text-left">Trace ID</th>
                      <th className="p-2 text-left">Cust WO#</th>
                      <th className="p-2 text-left">Challan</th>
                      <th className="p-2 text-left">Cust DC</th>
                      <th className="p-2 text-left">Cust PO</th>
                      <th className="p-2 text-left">Godown</th>
                      <th className="p-2 text-right">Available</th>
                      <th className="p-2 text-right w-24">Dispatch Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {previewItems
                      .map((item, idx) => ({ item, idx }))
                      .filter(({ item }) => {
                        const f = previewItems._qualityFilter;
                        if (!f || f === 'all') return true;
                        if (f === 'ok') return item.quality_status === 'ok';
                        if (f === 'not_ok') return item.quality_status === 'not_ok';
                        if (f === 'returned_as_is') return item.quality_status === 'returned_as_is';
                        return true;
                      })
                      .map(({ item, idx }) => (
                      <tr key={idx} className={`${item.selected ? '' : 'opacity-40 bg-muted/20'} ${item.quality_status === 'not_ok' ? 'bg-red-50/40' : ''}`}>
                        <td className="p-2 text-center">
                          <input type="checkbox" className="rounded"
                            checked={item.selected}
                            onChange={() => togglePreviewItem(idx)}
                            data-testid={`manifest-item-check-${idx}`}
                          />
                        </td>
                        <td className="p-2">
                          <div className="font-medium flex items-center gap-1.5 flex-wrap">
                            <EntityLink type="item" id={item.item_id}>{item.item_name}</EntityLink>
                            {item.is_foc && (
                              <span
                                className="text-[9px] px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 font-semibold"
                                title="Free of charge — not added to invoice totals"
                                data-testid={`foc-badge-${idx}`}
                              >FOC</span>
                            )}
                            {item.item_type && item.is_foc && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-700">
                                {item.item_type}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground font-mono"><EntityLink type="item" id={item.item_id} className="font-mono text-[10px] text-muted-foreground">{item.item_unique_id}</EntityLink></div>
                        </td>
                        <td className="p-2 text-center">
                          {item.quality_status === 'not_ok' ? (
                            <Badge variant="destructive" className="text-[9px] px-1.5 py-0" data-testid={`quality-badge-notok-${idx}`}>
                              <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Rejection
                            </Badge>
                          ) : item.quality_status === 'returned_as_is' ? (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50" data-testid={`quality-badge-returned-${idx}`}>
                              <RotateCcw className="w-2.5 h-2.5 mr-0.5" /> Return As Is
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-green-300 text-green-700 bg-green-50" data-testid={`quality-badge-ok-${idx}`}>
                              <ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> Finished Goods
                            </Badge>
                          )}
                        </td>
                        <td className="p-2">
                          {editingPreviewIdx === idx ? (
                            <Input
                              value={item.traceability_id || ''}
                              onChange={e => updatePreviewItemField(idx, 'traceability_id', e.target.value)}
                              placeholder="Customer WO#"
                              className="h-6 text-[10px] w-28 font-mono"
                              data-testid={`preview-edit-trace-${idx}`}
                            />
                          ) : (
                            <div className="flex items-center gap-1">
                              {item.traceability_id || item.wo_number ? (
                                <span className="font-mono text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded" title={item.traceability_id_description || ''}>
                                  {item.wo_number && !item.traceability_id ? `Work Order: ${item.wo_number}` : item.traceability_id}
                                  {item.wo_number && item.traceability_id ? ` (WO: ${item.wo_number})` : ''}
                                </span>
                              ) : item.traceability_id_description ? (
                                <span className="text-[10px] text-amber-700 italic" title={item.traceability_id_description}>
                                  {item.traceability_id_description}
                                </span>
                              ) : <span className="text-[10px] text-muted-foreground">-</span>}
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => setEditingPreviewIdx(idx)}
                                  className="text-muted-foreground hover:text-blue-600 transition-colors"
                                  title="Edit trace / DC / PO"
                                  data-testid={`preview-edit-btn-${idx}`}
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-[10px]">
                          {editingPreviewIdx === idx ? (
                            <Input
                              value={item.customer_wo_number || ''}
                              onChange={e => updatePreviewItemField(idx, 'customer_wo_number', e.target.value)}
                              placeholder="Customer WO#"
                              className="h-6 text-[10px] w-24 font-mono"
                              data-testid={`preview-edit-custwo-${idx}`}
                            />
                          ) : item.customer_wo_number ? (
                            <span className="font-mono text-purple-700 bg-purple-50 px-1 py-0.5 rounded">{item.customer_wo_number}</span>
                          ) : <span className="text-muted-foreground">-</span>}
                        </td>
                        <td className="p-2 text-[10px]">{item.inbound_challan_number || '-'}</td>
                        <td className="p-2 text-[10px]">
                          {editingPreviewIdx === idx ? (
                            <Input
                              value={item.customer_challan_number || ''}
                              onChange={e => updatePreviewItemField(idx, 'customer_challan_number', e.target.value)}
                              placeholder="Customer DC#"
                              className="h-6 text-[10px] w-24 font-mono"
                              data-testid={`preview-edit-dc-${idx}`}
                            />
                          ) : (item.customer_challan_number || '-')}
                        </td>
                        <td className="p-2 text-[10px]" data-testid={`preview-po-rate-${idx}`}>
                          {editingPreviewIdx === idx ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={item.customer_po_number || ''}
                                onChange={e => updatePreviewItemField(idx, 'customer_po_number', e.target.value)}
                                placeholder="Customer PO#"
                                className="h-6 text-[10px] w-24 font-mono"
                                data-testid={`preview-edit-po-${idx}`}
                              />
                              <button
                                type="button"
                                onClick={() => setEditingPreviewIdx(null)}
                                className="text-green-600 hover:text-green-700 transition-colors"
                                title="Done"
                                data-testid={`preview-edit-done-${idx}`}
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : item.customer_po_number ? (
                            <span
                              className="font-mono inline-flex items-center gap-1 text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded"
                              title={`Customer PO ${item.customer_po_number}${item.rate ? ` · Rate ${fmtLineRate(item)}` : ''}`}
                            >
                              {item.customer_po_number}
                              {item.rate > 0 ? (
                                <span className="text-emerald-600 font-semibold">· {fmtLineRate(item)}</span>
                              ) : null}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-2 text-[10px]">{item.godown_name}</td>
                        <td className="p-2 text-right font-mono">{item.available_qty}</td>
                        <td className="p-2 text-right">
                          <Input type="number" value={item.quantity}
                            onChange={e => updatePreviewItemQty(idx, e.target.value)}
                            className="h-6 text-xs w-20 text-right ml-auto"
                            max={item.available_qty}
                            disabled={!item.selected}
                            data-testid={`manifest-item-qty-${idx}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex gap-3">
                  <span>Total: {previewItems.filter(i => i.selected).reduce((s, i) => s + (i.quantity || 0), 0).toFixed(2)} units</span>
                  {previewItems.some(i => i.quality_status === 'not_ok' && i.selected) && (
                    <span className="text-red-600">
                      ({previewItems.filter(i => i.quality_status === 'not_ok' && i.selected).length} rejection items)
                    </span>
                  )}
                  {previewItems.some(i => i.quality_status === 'returned_as_is' && i.selected) && (
                    <span className="text-amber-600">
                      ({previewItems.filter(i => i.quality_status === 'returned_as_is' && i.selected).length} return-as-is items)
                    </span>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setPreviewStep(false)}>Back</Button>
                <Button size="sm" onClick={handleCreateManifest} disabled={creatingManifest || previewItems.filter(i => i.selected).length === 0}
                  data-testid="save-manifest-btn">
                  {creatingManifest ? 'Creating...' : `Create Manifest (${previewItems.filter(i => i.selected).length} items)`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Scrap Return Dialog */}
      <Dialog open={createScrapReturnOpen} onOpenChange={setCreateScrapReturnOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Recycle className="w-4 h-4" /> Create Scrap Return
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Customer *</Label>
              <SearchableSelect
                options={customerOptions}
                value={scrapReturnCustomerId}
                onSelect={onScrapCustomerSelect}
                placeholder="Select customer..."
                emptyText="No customers."
                testId="scrap-customer-select"
              />
            </div>

            {scrapReturnCustomerId && (
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Select Scrap Entries to Return</Label>
                  <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" title="Include scrap already returned/dispatched">
                    <Checkbox
                      checked={scrapShowAll}
                      onCheckedChange={(v) => { const nv = !!v; setScrapShowAll(nv); loadCustomerScrap(undefined, { showAll: nv }); }}
                      data-testid="scrap-show-all-toggle"
                    />
                    Show all (incl. returned)
                  </label>
                </div>

                {/* Filter by Customer DC# / Customer PO# — pulls EVERY matching scrap entry (no row cap) */}
                <div className="flex flex-wrap items-end gap-2 mt-2">
                  <div className="flex-1 min-w-[120px]">
                    <Label className="text-[10px] text-muted-foreground">Filter Cust DC#</Label>
                    <Input
                      className="h-7 text-xs"
                      placeholder="e.g. 2"
                      value={scrapFilterDc}
                      onChange={e => setScrapFilterDc(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') loadCustomerScrap(); }}
                      data-testid="scrap-filter-dc-input"
                    />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <Label className="text-[10px] text-muted-foreground">Filter Cust PO#</Label>
                    <Input
                      className="h-7 text-xs"
                      placeholder="e.g. 121212"
                      value={scrapFilterPo}
                      onChange={e => setScrapFilterPo(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') loadCustomerScrap(); }}
                      data-testid="scrap-filter-po-input"
                    />
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => loadCustomerScrap()} data-testid="scrap-filter-apply-btn">
                    Apply
                  </Button>
                  {(scrapFilterDc || scrapFilterPo) && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setScrapFilterDc(''); setScrapFilterPo(''); loadCustomerScrap(undefined, { dc: '', po: '' }); }} data-testid="scrap-filter-clear-btn">
                      Clear
                    </Button>
                  )}
                </div>

                {selectedCustomerDc && (
                  <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded mt-2 mb-1">
                    Selected Customer DC: <span className="font-semibold">{selectedCustomerDc}</span> — All entries must belong to this DC
                  </div>
                )}
                {loadingCustomerScrap ? (
                  <p className="text-xs text-muted-foreground mt-2">Loading scrap entries…</p>
                ) : customerPendingScrap.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="scrap-empty-msg">No scrap entries match for this customer</p>
                ) : (
                  <div className="border rounded-md mt-2 max-h-[280px] overflow-y-auto" data-testid="scrap-entries-grouped">
                    {(() => {
                      // Group by Customer DC# (entries with no DC fall into a single bucket)
                      const groups = {};
                      const order = [];
                      customerPendingScrap.forEach(e => {
                        const dc = e.customer_challan_number || '';
                        const key = dc || '__none__';
                        if (!groups[key]) { groups[key] = { dc, po: e.customer_po_number || '', entries: [] }; order.push(key); }
                        if (!groups[key].po && e.customer_po_number) groups[key].po = e.customer_po_number;
                        groups[key].entries.push(e);
                      });
                      return order.map(key => {
                        const g = groups[key];
                        const groupWeight = g.entries.reduce((s, x) => s + (x.actual_weight || 0), 0);
                        return (
                          <div key={key} data-testid={`scrap-group-${g.dc || 'no-dc'}`}>
                            <div className="flex items-center justify-between gap-3 bg-muted px-2.5 py-1.5 sticky top-0 z-10 border-b">
                              <span className="text-[11px] font-semibold">
                                Cust DC: <span className="font-mono">{g.dc || '—'}</span>
                                {g.po ? <span className="text-muted-foreground"> · Cust PO: <span className="font-mono">{g.po}</span></span> : null}
                              </span>
                              <span className="text-[10px] text-muted-foreground whitespace-nowrap pl-2">{g.entries.length} entries · {groupWeight.toFixed(2)} kg</span>
                            </div>
                            <table className="w-full text-xs">
                              <tbody className="divide-y">
                                {g.entries.map(entry => {
                                  const entryDc = entry.customer_challan_number || '';
                                  const isReturned = (entry.status && entry.status !== 'pending');
                                  const dcMismatch = selectedCustomerDc && entryDc && selectedCustomerDc !== entryDc;
                                  const isDisabled = isReturned || dcMismatch;
                                  return (
                                    <tr
                                      key={entry.id}
                                      className={`hover:bg-muted/30 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                      onClick={() => !isDisabled && toggleScrapEntry(entry.id)}
                                      title={isReturned ? `Already returned (${entry.return_number || ''})` : (dcMismatch ? `Belongs to different DC (${entryDc})` : '')}
                                    >
                                      <td className="p-2 w-8">
                                        <Checkbox
                                          checked={selectedScrapEntries.includes(entry.id)}
                                          onCheckedChange={() => !isDisabled && toggleScrapEntry(entry.id)}
                                          disabled={isDisabled}
                                        />
                                      </td>
                                      <td className="p-2 font-mono text-[10px]">{entry.customer_wo_number || '-'}</td>
                                      <td className="p-2 font-mono text-[10px]">{entry.wo_number}</td>
                                      <td className="p-2 text-[10px]">{entry.item_name?.slice(0, 20)}</td>
                                      <td className="p-2 text-center font-medium">{entry.actual_weight?.toFixed(2) || 0}</td>
                                      <td className="p-2 text-right">
                                        {isReturned && <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-300">{entry.status === 'dispatched' ? 'Returned' : entry.status}</Badge>}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {selectedScrapEntries.length > 0 && (
                  <div className="flex justify-between items-center mt-2 p-2 bg-orange-50 rounded">
                    <span className="text-xs">Selected: {selectedScrapEntries.length} entries</span>
                    <span className="text-sm font-bold text-orange-700">{selectedScrapWeight.toFixed(2)} kg</span>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea 
                value={scrapReturnRemarks} 
                onChange={e => setScrapReturnRemarks(e.target.value)}
                className="text-xs min-h-[60px]" 
                placeholder="Optional notes..." 
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setCreateScrapReturnOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreateScrapReturn} disabled={creatingScrapReturn || selectedScrapEntries.length === 0} data-testid="save-scrap-return-btn">
              {creatingScrapReturn ? 'Creating...' : `Return ${selectedScrapWeight.toFixed(2)} kg`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Manifest Dialog */}
      <Dialog open={viewManifestOpen} onOpenChange={setViewManifestOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              {selectedManifest?.manifest_number || 'Loading...'}
              {selectedManifest && getManifestStatusBadge(selectedManifest.status)}
            </DialogTitle>
          </DialogHeader>
          
          {loadingManifest ? (
            <div className="p-8 text-center text-muted-foreground">Loading manifest...</div>
          ) : selectedManifest ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-3 bg-muted/30 rounded-md">
                <div>
                  <p className="text-[10px] text-muted-foreground">Customer</p>
                  <p className="text-sm font-medium">{selectedManifest.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedManifest.customer_code}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Created</p>
                  <p className="text-sm">{selectedManifest.created_at?.slice(0, 10)}</p>
                  <p className="text-xs text-muted-foreground">by {selectedManifest.created_by_name}</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="p-2 bg-blue-50 rounded text-center flex-1">
                  <p className="text-lg font-bold text-blue-700">{selectedManifest.total_items}</p>
                  <p className="text-[10px] text-blue-600">Items</p>
                </div>
                <div className="p-2 bg-green-50 rounded text-center flex-1">
                  <p className="text-lg font-bold text-green-700">{selectedManifest.total_quantity}</p>
                  <p className="text-[10px] text-green-600">Total Qty</p>
                </div>
                {selectedManifest.emailed_at && (
                  <div className="p-2 bg-amber-50 rounded text-center flex-1">
                    <p className="text-xs font-medium text-amber-700">Emailed</p>
                    <p className="text-[10px] text-amber-600">{selectedManifest.emailed_to}</p>
                  </div>
                )}
              </div>

              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-2 text-left font-medium">#</th>
                      <th className="p-2 text-left font-medium">Item</th>
                      <th className="p-2 text-left font-medium">Part No</th>
                      <th className="p-2 text-center font-medium">Status</th>
                      <th className="p-2 text-left font-medium">Trace ID</th>
                      <th className="p-2 text-left font-medium">Cust WO#</th>
                      <th className="p-2 text-center font-medium">Qty</th>
                      <th className="p-2 text-left font-medium">Godown</th>
                      <th className="p-2 text-left font-medium">Inbound Challan</th>
                      <th className="p-2 text-left font-medium">Cust DC</th>
                      <th className="p-2 text-left font-medium">Cust PO</th>
                      <th className="p-2 text-left font-medium">PO</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedManifest.items?.map((item, idx) => (
                      <tr key={idx} className={`hover:bg-muted/30 ${item.quality_status === 'not_ok' ? 'bg-red-50/40' : item.quality_status === 'returned_as_is' ? 'bg-amber-50/40' : ''}`}>
                        <td className="p-2">{idx + 1}</td>
                        <td className="p-2">{item.item_name}</td>
                        <td className="p-2 font-mono">{item.item_unique_id}</td>
                        <td className="p-2 text-center">
                          {item.quality_status === 'not_ok' ? (
                            <Badge variant="destructive" className="text-[9px] px-1.5 py-0">
                              <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Rejection
                            </Badge>
                          ) : item.quality_status === 'returned_as_is' ? (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50">
                              <RotateCcw className="w-2.5 h-2.5 mr-0.5" /> Return As Is
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-green-300 text-green-700 bg-green-50">
                              <ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> OK
                            </Badge>
                          )}
                        </td>
                        <td className="p-2">
                          {item.traceability_id || item.wo_number ? (
                            <span className="font-mono text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded" title={item.traceability_id_description || ''}>
                              {item.wo_number && !item.traceability_id ? `Work Order: ${item.wo_number}` : item.traceability_id}
                              {item.wo_number && item.traceability_id ? ` (WO: ${item.wo_number})` : ''}
                            </span>
                          ) : <span className="text-[10px] text-muted-foreground">-</span>}
                        </td>
                        <td className="p-2 text-[10px]">
                          {item.customer_wo_number ? (
                            <span className="font-mono text-purple-700 bg-purple-50 px-1 py-0.5 rounded">{item.customer_wo_number}</span>
                          ) : <span className="text-muted-foreground">-</span>}
                        </td>
                        <td className="p-2 text-center">{item.quantity}</td>
                        <td className="p-2 text-[10px]">{item.godown_name}</td>
                        <td className="p-2 font-mono text-[10px]">{item.inbound_challan_number}</td>
                        <td className="p-2 text-[10px]">{item.customer_challan_number || '-'}</td>
                        <td className="p-2 text-[10px]" data-testid={`saved-po-rate-${item.item_id}`}>
                          {item.customer_po_number ? (
                            <span
                              className="font-mono inline-flex items-center gap-1 text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded"
                              title={`Customer PO ${item.customer_po_number}${item.rate ? ` · Rate ${fmtLineRate(item)}` : ''}`}
                            >
                              {item.customer_po_number}
                              {item.rate > 0 ? (
                                <span className="text-emerald-600 font-semibold">· {fmtLineRate(item)}</span>
                              ) : null}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-2 text-[10px]">{item.po_numbers || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedManifest.dispatch_challans?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium mb-2">Created Dispatch Challans</h4>
                  <div className="space-y-1.5">
                    {selectedManifest.dispatch_challans.map(dc => (
                      <div key={dc.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                        <div className="flex items-center gap-2">
                          <Badge variant={dc.challan_category === 'return' ? 'destructive' : 'outline'} className="text-[10px]">
                            {dc.dc_number}
                            {dc.challan_category === 'return' && <AlertTriangle className="w-3 h-3 ml-1" />}
                            {dc.reconciliation_status === 'reconciled' && <FileCheck className="w-3 h-3 ml-1 text-green-600" />}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {dc.challan_category === 'return' ? 'Return' : 'Delivery'}
                          </span>
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" 
                          onClick={() => downloadChallanPDF(dc.id, dc.dc_number)}
                          data-testid={`download-challan-${dc.dc_number}`}>
                          <Download className="w-3 h-3 mr-1" /> Download Challan
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 flex-wrap pt-2 border-t">
                <Button size="sm" variant="outline" onClick={() => downloadManifestPDF(selectedManifest.id, selectedManifest.manifest_number)}>
                  <Download className="w-3 h-3 mr-1" /> Download PDF
                </Button>

                {selectedManifest.status !== 'challans_created' && (
                  <Button size="sm" variant="outline" onClick={openEmailDialog} data-testid="email-manifest-btn">
                    <Mail className="w-3 h-3 mr-1" /> {selectedManifest.emailed_at ? 'Resend Manifest Email' : 'Email Manifest'}
                  </Button>
                )}
                {selectedManifest.status === 'challans_created' && (
                  <Button size="sm" variant="outline" onClick={openEmailDialog} data-testid="resend-manifest-btn"
                    title="Resend the manifest email (e.g. if accounts didn't receive the original)">
                    <Mail className="w-3 h-3 mr-1" /> Resend Manifest Email
                    {(selectedManifest.manifest_email_count || 0) > 1 && (
                      <span className="ml-1 text-[9px] px-1 py-0 rounded border border-amber-300 text-amber-700 bg-amber-50">
                        sent {selectedManifest.manifest_email_count}x
                      </span>
                    )}
                  </Button>
                )}
                
                {(selectedManifest.status === 'emailed' || selectedManifest.status === 'draft') && selectedManifest.status !== 'challans_created' && (
                  <Button size="sm" onClick={handleCreateChallansFromManifest} disabled={creatingChallans}>
                    <Truck className="w-3 h-3 mr-1" /> {creatingChallans ? 'Creating...' : 'Create Challans'}
                  </Button>
                )}

                {selectedManifest.status !== 'dispatched_to_customer' && (
                  <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700" onClick={openOneClickDispatch} data-testid="one-click-dispatch-btn">
                    <Mail className="w-3 h-3 mr-1" /> One-Click Dispatch
                  </Button>
                )}
                
                {selectedManifest.status === 'draft' && (
                  <Button size="sm" variant="destructive" onClick={() => deleteManifest(selectedManifest.id)}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* One-Click Dispatch Dialog */}
      <Dialog open={oneClickOpen} onOpenChange={setOneClickOpen}>
        <DialogContent className="max-w-md" data-testid="one-click-dispatch-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">One-Click Dispatch to Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5 text-[11px] text-emerald-800">
              This will create the consolidated Delivery Challan (if not already) and email it
              (with embedded Finished-Goods tags + any uploaded invoice) to the customer in one step.
            </div>
            <div>
              <Label className="text-xs">Customer Email *</Label>
              <Input value={oneClickForm.to_email} onChange={e => setOneClickForm(f => ({ ...f, to_email: e.target.value }))} className="h-8 text-xs" data-testid="one-click-to-email" />
            </div>
            <div>
              <Label className="text-xs">CC (comma separated)</Label>
              <Input value={oneClickForm.cc_email} onChange={e => setOneClickForm(f => ({ ...f, cc_email: e.target.value }))} className="h-8 text-xs" />
              <p className="text-[10px] text-muted-foreground mt-1">
                Standard CC: <span className="font-mono">toolroom@bhpl.co.in, ae@bhpl.co.in</span> (always added). Program-level default CC recipients are auto-added too.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Courier</Label>
                <Input value={oneClickForm.courier_name} onChange={e => setOneClickForm(f => ({ ...f, courier_name: e.target.value }))} className="h-8 text-xs" placeholder="Optional" />
              </div>
              <div>
                <Label className="text-xs">Tracking #</Label>
                <Input value={oneClickForm.tracking_number} onChange={e => setOneClickForm(f => ({ ...f, tracking_number: e.target.value }))} className="h-8 text-xs" placeholder="Optional" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={oneClickForm.subject} onChange={e => setOneClickForm(f => ({ ...f, subject: e.target.value }))} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Message (optional)</Label>
              <Textarea value={oneClickForm.message} onChange={e => setOneClickForm(f => ({ ...f, message: e.target.value }))} rows={3} className="text-xs" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setOneClickOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={oneClickSending} onClick={handleOneClickDispatch} data-testid="one-click-dispatch-send-btn">
              {oneClickSending ? 'Dispatching...' : 'Dispatch & Email'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Manifest Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              {selectedManifest?.emailed_at ? 'Resend Manifest Email' : 'Email Manifest'}
            </DialogTitle>
            {selectedManifest?.emailed_at && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Last sent {selectedManifest.emailed_at?.slice(0, 16).replace('T', ' ')} to <span className="font-mono">{selectedManifest.emailed_to}</span>
                {(selectedManifest.manifest_email_count || 0) > 1 && (
                  <span className="ml-2 px-1.5 py-0 rounded border border-amber-300 text-amber-700 bg-amber-50 text-[10px]">
                    Sent {selectedManifest.manifest_email_count}× total
                  </span>
                )}
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">To Email *</Label>
              <Input 
                value={emailForm.to_email} 
                onChange={e => setEmailForm(f => ({ ...f, to_email: e.target.value }))}
                className="h-8 text-xs" 
                placeholder="accounts3@bhpl.co.in"
                data-testid="email-to"
              />
            </div>
            <div>
              <Label className="text-xs">CC (comma separated)</Label>
              <Input 
                value={emailForm.cc_email} 
                onChange={e => setEmailForm(f => ({ ...f, cc_email: e.target.value }))}
                className="h-8 text-xs" 
                placeholder="Optional CC emails"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Standard CC: <span className="font-mono">toolroom@bhpl.co.in, ae@bhpl.co.in</span> (always added). Program-level default CC recipients are auto-added too.
              </p>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input 
                value={emailForm.subject} 
                onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))}
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea 
                value={emailForm.message} 
                onChange={e => setEmailForm(f => ({ ...f, message: e.target.value }))}
                className="text-xs min-h-[100px]"
              />
            </div>
            <p className="text-[10px] text-muted-foreground">
              PDF manifest will be auto-attached to the email
            </p>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleEmailManifest} disabled={sendingEmail} data-testid="send-email-btn">
              <Mail className="w-3 h-3 mr-1" /> {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create DC Dialog (Manual) */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">New Dispatch Challan (Manual)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Customer *</Label>
                <SearchableSelect
                  options={customerOptions}
                  value={dcForm.customer_id}
                  onSelect={onCustomerSelect}
                  placeholder="Search customers..."
                  emptyText="No customers found."
                  testId="dc-customer-select"
                />
              </div>
              <div>
                <Label className="text-xs">Dispatch Type</Label>
                <Select value={dcForm.dispatch_type} onValueChange={v => setDcForm(f => ({ ...f, dispatch_type: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="dc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="delivery_challan">Delivery Challan</SelectItem>
                    <SelectItem value="invoice">With Invoice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {dcForm.customer_id && (
              <div className={`p-3 border rounded-md ${openInboundChallans.length > 0 ? 'bg-blue-50 border-blue-200' : 'bg-muted/30 border-muted'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className={`w-4 h-4 ${openInboundChallans.length > 0 ? 'text-blue-600' : 'text-muted-foreground'}`} />
                  <Label className={`text-xs font-medium ${openInboundChallans.length > 0 ? 'text-blue-700' : 'text-muted-foreground'}`}>
                    Link to Job Work (Inbound Challan)
                  </Label>
                </div>
                {openInboundChallans.length > 0 ? (
                  <>
                    <SearchableSelect
                      options={[
                        { value: 'none', label: 'None (Regular Dispatch)', searchStr: 'none regular' },
                        ...openInboundChallans.map(ic => ({
                          value: ic.id,
                          label: `${ic.challan_number} (${ic.items?.length || 0} items)${ic.due_date ? ` - Due: ${ic.due_date}` : ''}`,
                          sub: ic.items?.map(i => i.item_name).join(', ') || '',
                          searchStr: `${ic.challan_number} ${ic.items?.map(i => `${i.item_name} ${i.item_unique_id}`).join(' ') || ''} ${ic.due_date || ''}`,
                        }))
                      ]}
                      value={dcForm.inbound_challan_id || 'none'}
                      onSelect={onInboundChallanSelect}
                      placeholder="Search challan number..."
                      emptyText="No challans found."
                      testId="dc-inbound-select"
                    />
                    <p className="text-[10px] text-blue-600 mt-1">Link this dispatch to close a job work challan</p>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">No open job work challans for this customer</p>
                )}
              </div>
            )}

            {dcForm.dispatch_type === 'invoice' && (
              <div>
                <Label className="text-xs">Invoice Number *</Label>
                <Input value={dcForm.invoice_number} onChange={e => setDcForm(f => ({ ...f, invoice_number: e.target.value }))}
                  className="h-8 text-xs" data-testid="dc-invoice" />
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium">Items to Dispatch</Label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={includeFixedAssets}
                      onChange={e => setIncludeFixedAssets(e.target.checked)}
                      className="h-3.5 w-3.5 accent-slate-600" data-testid="dc-include-fixed-assets" />
                    Include fixed assets
                  </label>
                  <Button type="button" size="sm" variant="outline" className="h-6 text-[10px]" onClick={addItem}>
                    <Plus className="w-3 h-3 mr-0.5" /> Add Item
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {dcItems.map((di, idx) => (
                  <Card key={idx} className="p-3 bg-muted/30">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4">
                        <Label className="text-[10px] text-muted-foreground">Item</Label>
                        <SearchableSelect
                          options={itemOptions}
                          value={di.item_id}
                          onSelect={v => updateItem(idx, 'item_id', v)}
                          placeholder="Search items..."
                          emptyText="No items."
                          testId={`dc-item-${idx}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] text-muted-foreground">Godown</Label>
                        <Select value={di.godown_id} onValueChange={v => updateItem(idx, 'godown_id', v)}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`dc-godown-${idx}`}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {godownOptions.map(g => (
                              <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1">
                        <Label className="text-[10px] text-muted-foreground">Avail</Label>
                        <div className="h-8 flex items-center text-xs text-muted-foreground">
                          {di.item_id && di.godown_id ? getAvailableStock(di.item_id, di.godown_id) : '-'}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] text-muted-foreground">Qty</Label>
                        <Input type="number" min="0" value={di.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                          className="h-8 text-xs" data-testid={`dc-qty-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] text-muted-foreground">Remarks</Label>
                        <Input value={di.remarks} onChange={e => updateItem(idx, 'remarks', e.target.value)}
                          className="h-8 text-xs" placeholder="Optional" />
                      </div>
                      <div className="col-span-1">
                        {dcItems.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeItem(idx)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea value={dcForm.remarks} onChange={e => setDcForm(f => ({ ...f, remarks: e.target.value }))}
                className="text-xs min-h-[60px]" placeholder="Optional notes..." />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreate} disabled={creating} data-testid="save-dispatch-btn">
              {creating ? 'Creating...' : 'Create Dispatch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconcile Dialog */}
      <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Reconcile: {reconcileChallan?.dc_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Reconciliation Type</Label>
              <Select value={reconcileForm.reconciliation_type} onValueChange={v => setReconcileForm(f => ({ ...f, reconciliation_type: v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="reconcile-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Link to Invoice</SelectItem>
                  <SelectItem value="rejection_invoice">Rejection Invoice (Debit Note)</SelectItem>
                  <SelectItem value="foc">Free of Charge (FOC)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(reconcileForm.reconciliation_type === 'invoice' || reconcileForm.reconciliation_type === 'rejection_invoice') && (
              <>
                <div>
                  <Label className="text-xs">Invoice Number *</Label>
                  <Input value={reconcileForm.invoice_number} onChange={e => setReconcileForm(f => ({ ...f, invoice_number: e.target.value }))}
                    className="h-8 text-xs" placeholder="Enter invoice number" data-testid="reconcile-invoice" />
                </div>
                <div>
                  <Label className="text-xs">Upload Invoice (PDF/JPG)</Label>
                  <Input 
                    type="file" 
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setInvoiceFile(e.target.files?.[0] || null)}
                    className="h-8 text-xs"
                  />
                  {invoiceFile && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Selected: {invoiceFile.name}
                    </p>
                  )}
                </div>
              </>
            )}

            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea value={reconcileForm.remarks} onChange={e => setReconcileForm(f => ({ ...f, remarks: e.target.value }))}
                className="text-xs min-h-[60px]" placeholder="Optional notes..." />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setReconcileOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleReconcile} disabled={uploadingInvoice} data-testid="confirm-reconcile-btn">
              {uploadingInvoice ? 'Uploading...' : 'Confirm Reconciliation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Delivery Challan Dialog */}
      <Dialog open={!!cancelChallanTarget} onOpenChange={(o) => { if (!o) { setCancelChallanTarget(null); setCancelReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="cancel-challan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-4 h-4" /> Cancel Delivery Challan
            </DialogTitle>
          </DialogHeader>
          {cancelChallanTarget && (
            <div className="space-y-3">
              <div className="text-sm bg-red-50 border border-red-200 rounded p-3 text-red-800">
                You are about to cancel <span className="font-mono font-semibold">{cancelChallanTarget.dc_number}</span>.
                The dispatched stock ({cancelChallanTarget.total_items} item(s)) will be <strong>restored back to its source godown</strong> (and trace lots reversed). This cannot be undone.
              </div>
              <div>
                <Label className="text-xs">Cancellation reason <span className="text-red-500">*</span></Label>
                <Input
                  className="h-8 text-sm mt-1"
                  placeholder="e.g. Wrong items dispatched / customer rejected pickup"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  data-testid="cancel-challan-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setCancelChallanTarget(null); setCancelReason(''); }} disabled={cancellingChallan}>Keep Challan</Button>
            <Button variant="destructive" size="sm" onClick={submitCancelChallan} disabled={cancellingChallan} data-testid="cancel-challan-confirm-btn">
              {cancellingChallan ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
              Cancel & Restore Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Customer Email Dialog */}
      <Dialog open={customerEmailOpen} onOpenChange={setCustomerEmailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4" /> Send Dispatch Email to Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Customer *</Label>
              <SearchableSelect
                options={customerOptions}
                value={emailCustomerId}
                onSelect={onEmailCustomerSelect}
                placeholder="Select customer..."
                emptyText="No customers."
                testId="email-customer-select"
              />
            </div>

            {emailCustomerId && (
              <>
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="text-xs">Select Challans to Include *</Label>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-already-emailed-toggle">
                        <Checkbox
                          checked={showAlreadyEmailed}
                          onCheckedChange={(v) => {
                            const checked = !!v;
                            setShowAlreadyEmailed(checked);
                            if (!checked) {
                              setSelectedEmailChallans(prev => prev.filter(id => {
                                const c = emailChallans.find(x => x.id === id);
                                return c && !c.customer_emailed_at;
                              }));
                            }
                          }}
                        />
                        Show already emailed
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-old-unsent-toggle">
                        <Checkbox
                          checked={showOldUnsent}
                          onCheckedChange={(v) => {
                            const checked = !!v;
                            setShowOldUnsent(checked);
                            if (!checked) {
                              const cutoffMs = Date.now() - 3 * 24 * 60 * 60 * 1000;
                              setSelectedEmailChallans(prev => prev.filter(id => {
                                const c = emailChallans.find(x => x.id === id);
                                if (!c) return false;
                                if (c.customer_emailed_at) return true;
                                const ts = new Date(c.created_at || c.dispatch_date || 0).getTime();
                                return !ts || ts >= cutoffMs;
                              }));
                            }
                          }}
                        />
                        Show old unsent (&gt;3 days)
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="include-non-invoiced-toggle">
                        <Checkbox
                          checked={includeNonInvoiced}
                          onCheckedChange={(v) => {
                            const checked = !!v;
                            setIncludeNonInvoiced(checked);
                            if (!checked && !includeNonReconciled) {
                              setSelectedEmailChallans(prev => prev.filter(id => {
                                const c = emailChallans.find(x => x.id === id);
                                return c && !isNonInvoicedChallan(c);
                              }));
                            }
                            if (emailCustomerId) loadEmailChallans(emailCustomerId, { nonInvoiced: checked, nonReconciled: includeNonReconciled });
                          }}
                        />
                        Include non-invoiced (FOC / Return-as-is)
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="include-non-reconciled-toggle">
                        <Checkbox
                          checked={includeNonReconciled}
                          onCheckedChange={(v) => {
                            const checked = !!v;
                            setIncludeNonReconciled(checked);
                            if (!checked) {
                              // Drop any selected DCs that are non-reconciled (unless still shown via non-invoiced)
                              setSelectedEmailChallans(prev => prev.filter(id => {
                                const c = emailChallans.find(x => x.id === id);
                                if (!c) return false;
                                if (c.reconciliation_status === 'reconciled') return true;
                                if (includeNonInvoiced && isNonInvoicedChallan(c)) return true;
                                if (c.challan_category === 'return') return true;
                                return false;
                              }));
                            }
                            if (emailCustomerId) loadEmailChallans(emailCustomerId, { nonInvoiced: includeNonInvoiced, nonReconciled: checked });
                          }}
                        />
                        Include non-reconciled dispatches
                      </label>
                    </div>
                  </div>
                  {(() => {
                    const cutoffMs = Date.now() - 3 * 24 * 60 * 60 * 1000;
                    const isOldUnsent = (c) => {
                      if (c.customer_emailed_at) return false;
                      const ts = new Date(c.created_at || c.dispatch_date || 0).getTime();
                      return ts && ts < cutoffMs;
                    };
                    const visibleChallans = emailChallans.filter(c => {
                      if (!showAlreadyEmailed && c.customer_emailed_at) return false;
                      if (!showOldUnsent && isOldUnsent(c)) return false;
                      return true;
                    });
                    const hiddenEmailedCount = !showAlreadyEmailed
                      ? emailChallans.filter(c => c.customer_emailed_at).length
                      : 0;
                    const hiddenOldCount = !showOldUnsent
                      ? emailChallans.filter(isOldUnsent).length
                      : 0;
                    if (visibleChallans.length === 0) {
                      let msg;
                      if (emailChallans.length === 0) {
                        msg = 'No reconciled or return challans found for this customer. Tick "Include non-invoiced (FOC / Return-as-is)" above to show DCs sent without an invoice.';
                      } else if (hiddenEmailedCount > 0 && hiddenOldCount > 0) {
                        msg = `${hiddenEmailedCount} already-emailed and ${hiddenOldCount} old unsent challan(s) hidden. Toggle the filters above to view them.`;
                      } else if (hiddenEmailedCount > 0) {
                        msg = `All ${hiddenEmailedCount} eligible challan(s) have already been emailed. Enable "Show already emailed" to resend.`;
                      } else if (hiddenOldCount > 0) {
                        msg = `All ${hiddenOldCount} unsent challan(s) are older than 3 days. Enable "Show old unsent" to view them.`;
                      } else {
                        msg = 'No challans match current filters.';
                      }
                      return (
                        <p className="text-xs text-muted-foreground mt-2 p-3 bg-muted/50 rounded">{msg}</p>
                      );
                    }
                    return (
                      <>
                        <div className="border rounded-md mt-2 max-h-[200px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-muted/50 sticky top-0">
                              <tr>
                                <th className="p-2 w-8"></th>
                                <th className="p-2 text-left">DC Number</th>
                                <th className="p-2 text-left">Invoice</th>
                                <th className="p-2 text-left">Date</th>
                                <th className="p-2 text-center">Items</th>
                                <th className="p-2 text-left">Email Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {visibleChallans.map(challan => (
                                <tr key={challan.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => toggleEmailChallan(challan.id)} data-testid={`email-challan-row-${challan.dc_number}`}>
                                  <td className="p-2">
                                    <Checkbox 
                                      checked={selectedEmailChallans.includes(challan.id)}
                                      onCheckedChange={() => toggleEmailChallan(challan.id)}
                                    />
                                  </td>
                                  <td className="p-2 font-mono">
                                    {challan.dc_number}
                                    {isOldUnsent(challan) && (
                                      <span className="ml-1 text-[9px] text-amber-700" title="Older than 3 days, never emailed">⏳</span>
                                    )}
                                  </td>
                                  <td className="p-2">{challan.invoice_number || '-'}</td>
                                  <td className="p-2">{challan.dispatch_date?.slice(0, 10)}</td>
                                  <td className="p-2 text-center">{challan.items?.length || 0}</td>
                                  <td className="p-2">
                                    {challan.customer_emailed_at ? (
                                      <span className="text-[10px] inline-block px-1.5 py-0.5 rounded border border-blue-300 text-blue-700 bg-blue-50" title={`Sent to ${challan.customer_emailed_to || ''}`}>
                                        Emailed {challan.customer_emailed_at.slice(0, 10)}
                                      </span>
                                    ) : (
                                      <span className="text-[10px] text-muted-foreground">Not sent</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {(hiddenEmailedCount > 0 || hiddenOldCount > 0) && (
                          <p className="text-[11px] text-muted-foreground mt-1">
                            {hiddenEmailedCount > 0 && <>{hiddenEmailedCount} already-emailed hidden. </>}
                            {hiddenOldCount > 0 && <>{hiddenOldCount} old unsent (&gt;3 days) hidden. </>}
                            Use the toggles above to include them.
                          </p>
                        )}
                      </>
                    );
                  })()}
                  {selectedEmailChallans.length > 0 && (
                    <p className="text-xs text-blue-600 mt-1">
                      {selectedEmailChallans.length} challan(s) selected
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">To Email *</Label>
                    <Input 
                      value={customerEmailForm.to_email} 
                      onChange={e => setCustomerEmailForm(f => ({ ...f, to_email: e.target.value }))}
                      className="h-8 text-xs" 
                      placeholder="customer@example.com"
                      data-testid="customer-email-to"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CC (comma separated)</Label>
                    <Input 
                      value={customerEmailForm.cc_email} 
                      onChange={e => setCustomerEmailForm(f => ({ ...f, cc_email: e.target.value }))}
                      className="h-8 text-xs" 
                      placeholder="Optional CC emails"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Standard CC: <span className="font-mono">toolroom@bhpl.co.in, ae@bhpl.co.in</span> (always added). Program-level default CC recipients are auto-added too.
                    </p>
                  </div>
                </div>

                <div className="p-3 border rounded-md bg-blue-50 border-blue-200 space-y-3">
                  <p className="text-xs font-medium text-blue-700">Shipping Details (Optional)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Courier Name</Label>
                      <Input 
                        value={customerEmailForm.courier_name} 
                        onChange={e => setCustomerEmailForm(f => ({ ...f, courier_name: e.target.value }))}
                        className="h-8 text-xs bg-white" 
                        placeholder="e.g. BlueDart, FedEx"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Tracking Number</Label>
                      <Input 
                        value={customerEmailForm.tracking_number} 
                        onChange={e => setCustomerEmailForm(f => ({ ...f, tracking_number: e.target.value }))}
                        className="h-8 text-xs bg-white" 
                        placeholder="AWB/Tracking #"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Subject (Optional - auto-generated if blank)</Label>
                  <Input 
                    value={customerEmailForm.subject} 
                    onChange={e => setCustomerEmailForm(f => ({ ...f, subject: e.target.value }))}
                    className="h-8 text-xs"
                    placeholder="Auto-generated if left blank"
                  />
                </div>

                <div>
                  <Label className="text-xs">Additional Message</Label>
                  <Textarea 
                    value={customerEmailForm.message} 
                    onChange={e => setCustomerEmailForm(f => ({ ...f, message: e.target.value }))}
                    className="text-xs min-h-[80px]"
                    placeholder="Optional additional message to include in email..."
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="include-pdf"
                    checked={customerEmailForm.include_challan_pdf}
                    onCheckedChange={checked => setCustomerEmailForm(f => ({ ...f, include_challan_pdf: checked }))}
                  />
                  <Label htmlFor="include-pdf" className="text-xs">Attach challan PDFs to email</Label>
                </div>

                {canIssueCoc && (
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="include-coc-bundle"
                      checked={customerEmailForm.include_coc_bundle}
                      onCheckedChange={checked => setCustomerEmailForm(f => ({ ...f, include_coc_bundle: checked }))}
                      data-testid="email-include-coc-checkbox"
                    />
                    <Label htmlFor="include-coc-bundle" className="text-xs leading-snug">
                      Attach Certificate of Conformance (one PDF per dispatch line / trace id)
                      <span className="block text-[10px] text-muted-foreground mt-0.5">
                        Only Admin / Quality Manager can attach COCs. Each COC is audit-logged.
                      </span>
                    </Label>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setCustomerEmailOpen(false)}>Cancel</Button>
            <Button 
              size="sm" 
              onClick={handleSendCustomerEmail} 
              disabled={sendingCustomerEmail || selectedEmailChallans.length === 0 || !customerEmailForm.to_email}
              data-testid="send-customer-email-btn"
            >
              <Mail className="w-3 h-3 mr-1" /> 
              {sendingCustomerEmail ? 'Sending...' : `Send Email (${selectedEmailChallans.length} challans)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Return Dialog */}
      <Dialog open={supplierReturnOpen} onOpenChange={v => { setSupplierReturnOpen(v); if (!v) { setSupplierReturnPreviewStep(false); setSupplierReturnItems([]); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="supplier-return-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{supplierReturnPreviewStep ? 'Review Return Items' : 'Return to Supplier'}</DialogTitle>
          </DialogHeader>
          
          {!supplierReturnPreviewStep ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Select Supplier *</Label>
                <SearchableSelect
                  options={suppliers.map(s => ({ value: s.id, label: s.supplier_name, sub: s.supplier_code || '', searchStr: `${s.supplier_name} ${s.supplier_code || ''}` }))}
                  value={supplierReturnId}
                  onSelect={v => setSupplierReturnId(v)}
                  placeholder="Search supplier..."
                  emptyText="No suppliers found."
                  testId="supplier-return-select"
                />
              </div>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-3">
                  <p className="text-xs text-amber-700">
                    <strong>Note:</strong> Only items that were quarantined or rejected during QC inspection and traced back to the selected supplier will appear.
                    Stock will be deducted from Quarantine/Rejection godowns and a delivery challan will be created for the return.
                  </p>
                </CardContent>
              </Card>
              <div>
                <Label className="text-xs">Remarks</Label>
                <Textarea value={supplierReturnRemarks} onChange={e => setSupplierReturnRemarks(e.target.value)}
                  className="text-xs min-h-[60px]" placeholder="Reason for return..." />
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setSupplierReturnOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={handleSupplierReturnPreview} disabled={supplierReturnPreviewLoading || !supplierReturnId} data-testid="supplier-return-preview-btn">
                  {supplierReturnPreviewLoading ? 'Loading...' : 'Preview Return Items'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <Card className="bg-slate-50">
                <CardContent className="p-3">
                  <p className="text-xs font-medium">
                    Supplier: {suppliers.find(s => s.id === supplierReturnId)?.supplier_name || ''}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {supplierReturnItems.filter(i => i.selected).length} of {supplierReturnItems.length} items selected for return
                  </p>
                </CardContent>
              </Card>
              
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {supplierReturnItems.map((item, idx) => (
                  <Card key={idx} className={`${item.selected ? 'border-blue-200' : 'opacity-50'}`} data-testid={`supplier-return-item-${idx}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={item.selected} onCheckedChange={() => toggleSupplierReturnItem(idx)} />
                        <div className="flex-1">
                          <p className="text-xs font-medium">{item.item_name}</p>
                          <div className="flex gap-2 text-[10px] text-muted-foreground">
                            <span>{item.item_unique_id}</span>
                            <span className={`font-medium ${item.godown_code === 'QUARANTINE' ? 'text-orange-600' : 'text-red-600'}`}>
                              {item.godown_name}
                            </span>
                            <span>Available: {item.available_qty} {item.unit_of_measure || 'Nos'}</span>
                          </div>
                        </div>
                        <div className="w-24">
                          <Input
                            type="number" min="0" max={item.available_qty} step="any"
                            value={item.quantity}
                            onChange={e => updateSupplierReturnItemQty(idx, e.target.value)}
                            className="h-7 text-xs"
                            disabled={!item.selected}
                            data-testid={`supplier-return-qty-${idx}`}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setSupplierReturnPreviewStep(false)}>Back</Button>
                <Button size="sm" onClick={handleCreateSupplierReturn}
                  disabled={creatingSupplierReturn || supplierReturnItems.filter(i => i.selected && i.quantity > 0).length === 0}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="create-supplier-return-confirm"
                >
                  {creatingSupplierReturn ? 'Creating...' : `Create Return DC (${supplierReturnItems.filter(i => i.selected && i.quantity > 0).length} items)`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
